// ===============================
//  FULL WORKING APP.JS (BASIC AUTH + OSM ONLY)
// ===============================

import React, { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import MapboxDraw from "mapbox-gl-draw";
import "maplibre-gl/dist/maplibre-gl.css";
// import { Protocol } from "pmtiles";
import "./App.css";
import "./components/Events/Events.css";
import "./responsive.css";
import { tegolaVectorConfig } from "./tegola-vector-config";

// Import modular components
import {
  SearchBar,
  RVGSidebar,
  RVGBottomCard,
  RoutingPanel,
  POISidebar,
  ZoomControls,
  RoadWidthPanel,
  MetadataModal,
  TruckAnimationPanel,
  DrawingPanel,
  MapDesigner,
  MapStylePanel,
  Building3DPanel,
  CustomScale,
  Attribution,
  Login,
  UserBadge,
  useKeycloak,
  FeedbackModal,
  RVGFeedbackModal,
  RouteStepMarkers
} from './components';

// Import custom hooks
import { useRoadColors, useRouting, usePOI, useReverseGeocode, useTruckAnimation, useStreetView, useRoadQuality, useRoadWidth, useMobile, useEvents } from './hooks';
import { useSearchTracking } from './hooks/useSearchTracking';
import { getMultiWaypointRoute } from './services/multiWaypointApi';
import { fetchUserFeatures, saveUserFeature, deleteUserFeature } from './services/api';
import { POI_CATEGORIES } from './utils/constants';
import { parseWKTGeometry, getGeometryCoordinates } from './utils/helpers';
import { DEFAULT_BUILDING_3D_SETTINGS, MIN_BUILDINGS_VISIBLE_ZOOM, loadBuilding3DSettings, saveBuilding3DSettings } from './utils/building3DSettings';
import { loadMapStyleFromStorage } from './utils/mapStylePersistence';
import { authFetch } from './lib/keycloak-api';

// Track if PMTiles protocol is registered
// let protocolRegistered = false;

function App() {
  // Keycloak authentication
  const { initialized, authenticated, keycloak } = useKeycloak();
  const isMobile = useMobile();
  const isMweb = isMobile || (typeof document !== 'undefined' && document.body.classList.contains('mweb'));

  // Map refs
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const searchResultMarkerRef = useRef(null);
  const drawRef = useRef(null);
  const pendingMapClickTimeoutRef = useRef(null);
  const lastPinnedLocationRef = useRef(null);
  const internalUrlCardInitializedRef = useRef(false);
  
  // UI state (modals, popups, etc.)
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  const [showTruckPanel, setShowTruckPanel] = useState(false);
  const [showDrawingPanel, setShowDrawingPanel] = useState(false);
  const [isRvgMode, setIsRvgMode] = useState(true);
  const [showMapDesigner, setShowMapDesigner] = useState(false);
  const [showMapStylePanel, setShowMapStylePanel] = useState(false);
  const [showBuilding3DPanel, setShowBuilding3DPanel] = useState(false);
  const [building3DSettings, setBuilding3DSettings] = useState(() => loadBuilding3DSettings());
  const [userLocation, setUserLocation] = useState(null);
  const [is3DEnabled, setIs3DEnabled] = useState(false);
  const [isUserBadgeOpen, setIsUserBadgeOpen] = useState(false);
  const [searchClearSignal, setSearchClearSignal] = useState(0);
  const [prefilledRoutingDestinationName, setPrefilledRoutingDestinationName] = useState('');
  const [routePointADetails, setRoutePointADetails] = useState(null);
  const [routePointBDetails, setRoutePointBDetails] = useState(null);
  const routeStopsRef = useRef([]);
  const [routingFocusTarget, setRoutingFocusTarget] = useState('source');
  const [routingActiveInput, setRoutingActiveInput] = useState('source');
  const [routingActiveWaypointIndex, setRoutingActiveWaypointIndex] = useState(null);
  const routingPanelRef = useRef(null);
  const routeStepMarkersRef = useRef(null);

  const handleActiveInputChange = useCallback((inputType, waypointIndex) => {
    setRoutingActiveInput(inputType);
    setRoutingActiveWaypointIndex(waypointIndex ?? null);
  }, []);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  // Drawing state
  const [userFeatures, setUserFeatures] = useState([]);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [drawingType, setDrawingType] = useState('polygon');

  // Feedback state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showRvgFeedbackModal, setShowRvgFeedbackModal] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('mweb', isMobile);
    return () => {
      document.body.classList.remove('mweb');
    };
  }, [isMobile]);

  // Helper function to fetch location details (used by routing hook)
  const fetchLocationDetails = useCallback(async (lat, lng) => {
    try {
      const formattedLat = parseFloat(lat).toFixed(6);
      const formattedLng = parseFloat(lng).toFixed(6);
      const url = `https://search-maps-pub-int.delhivery.com/reverseGeocoding?lat=${formattedLat}&long=${formattedLng}&building_visible=true`;

      const resp = await authFetch(url, {
        method: "GET",
        headers: {
          'Authorization': 'Basic ' + btoa('delhivery-ds:d31hiv3rwhy@321'),
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      }

      const response = await resp.json();

      // Handle new API format: response.data contains the actual data
      const data = response?.data || response;

      // The API returns data with fields: entity_name, entity_address (new) or display_name, display_address (old)
      if (data && (data.entity_name || data.display_name || data.entity_address || data.display_address || data.name || data.full_address)) {
        // Normalize the response to include both old and new field names for compatibility
        return {
          ...data,
          entity_name: data.entity_name || data.display_name || data.name,
          display_name: data.entity_name || data.display_name || data.name,
          entity_address: data.entity_address || data.display_address || data.full_address,
          display_address: data.entity_address || data.display_address || data.full_address,
          name: data.entity_name || data.display_name || data.name,
          rooftop_name: data.rooftop_name || data.apartment_name || data.entity_name || data.display_name || data.name
        };
      }
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  // Custom hooks for feature modules
  useRoadColors(mapRef); // Road colors hook (not actively used but initializes functionality)
  const routePointDragHandlerRef = useRef(null);
  const routing = useRouting(mapRef, fetchLocationDetails, currentLocationMarkerRef, routePointDragHandlerRef);
  const reverseGeocode = useReverseGeocode(mapRef);
  const handlePoiClick = useCallback((poiData) => {
    if (!poiData) {
      return;
    }

    const { name, amenity, address, lat, lon } = poiData;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }

    const normalizedData = {
      name: name || 'Unknown POI',
      entity_name: name || 'Unknown POI',
      display_name: name || 'Unknown POI',
      rooftop_name: name || 'Unknown POI',
      display_text: address || '',
      display_address: address || '',
      full_address: address || '',
      type: amenity || 'poi',
      distance_m: 0,
      location: {
        lat,
        lon,
        long: lon
      },
      clickedLocation: {
        lat,
        long: lon
      }
    };

    reverseGeocode.clearRvgPolygon();
    reverseGeocode.clearRvgMarkers();
    reverseGeocode.setPopupPosition({ lng: lon, lat });
    reverseGeocode.setPopupData(normalizedData);
    reverseGeocode.setShowPopup(true);
    reverseGeocode.setRvgResults([normalizedData]);
    reverseGeocode.setShowSidebar(false);
  }, [reverseGeocode]);
  const poi = usePOI(mapRef, currentZoom, {
    onPoiClick: handlePoiClick,
    priorityMarkerRefs: [
      routing.routeMarkersRef,       // route source/destination/stop pins
      searchResultMarkerRef,         // search result / RVG pin
      searchMarkerRef,               // search marker
      reverseGeocode.popupMarkerRef, // RVG popup marker
      currentLocationMarkerRef,      // user's current location blue dot
    ]
  });
  const truck = useTruckAnimation(mapRef);
  const streetView = useStreetView(mapRef);
  const roadQuality = useRoadQuality(mapRef);
  const events = useEvents(mapRef);
  const roadWidth = useRoadWidth(mapRef);
  const searchTracking = useSearchTracking();

  const handleSearchBarDirections = useCallback(() => {
    if (routing.isRoutingMode) {
      routing.toggleRoutingMode();
      return;
    }

    reverseGeocode.setShowSidebar(false);
    reverseGeocode.setRvgResults([]);
    reverseGeocode.setHighlightedResultIndex(null);
    reverseGeocode.clearRvgMarkers();

    if (searchMarkerRef.current) {
      searchMarkerRef.current.remove();
      searchMarkerRef.current = null;
    }
    if (searchResultMarkerRef.current) {
      searchResultMarkerRef.current.remove();
      searchResultMarkerRef.current = null;
    }

    routing.toggleRoutingMode();
  }, [routing, reverseGeocode]);

  // -----------------------------------
  // 🎨 DRAWING MODE FUNCTIONS
  // -----------------------------------

  // Load user features on mount
  useEffect(() => {
    const loadUserFeatures = async () => {
      try {
        const data = await fetchUserFeatures();
        setUserFeatures(data.features || []);
      } catch (error) {
        // Failed to load user features
      }
    };
    loadUserFeatures();
  }, []);



  // Initialize MapboxDraw when map is ready
  useEffect(() => {
    if (!mapRef.current || drawRef.current) return;

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      styles: [
        // Polygon fill
        {
          'id': 'gl-draw-polygon-fill',
          'type': 'fill',
          'filter': ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          'paint': {
            'fill-color': '#667eea',
            'fill-opacity': 0.3
          }
        },
        // Polygon outline
        {
          'id': 'gl-draw-polygon-stroke-active',
          'type': 'line',
          'filter': ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          'paint': {
            'line-color': '#667eea',
            'line-width': 2
          }
        },
        // Line
        {
          'id': 'gl-draw-line',
          'type': 'line',
          'filter': ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          'paint': {
            'line-color': '#667eea',
            'line-width': 3
          }
        },
        // Points
        {
          'id': 'gl-draw-point',
          'type': 'circle',
          'filter': ['all', ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 6,
            'circle-color': '#667eea'
          }
        },
        // Vertex points
        {
          'id': 'gl-draw-polygon-and-line-vertex-active',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'vertex'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 4,
            'circle-color': '#fff',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#667eea'
          }
        }
      ]
    });

    mapRef.current.addControl(draw, 'top-left');
    drawRef.current = draw;

    // Handle draw events
    mapRef.current.on('draw.create', (e) => {
      setCurrentDrawing(e.features[0]);
    });

    mapRef.current.on('draw.update', (e) => {
      setCurrentDrawing(e.features[0]);
    });

  }, []);

  const handleStartDrawing = (type) => {
    if (!drawRef.current) return;

    setDrawingType(type);
    setCurrentDrawing(null);

    // Clear any existing drawings
    drawRef.current.deleteAll();

    // Start drawing based on type
    if (type === 'polygon') {
      drawRef.current.changeMode('draw_polygon');
    } else if (type === 'road') {
      drawRef.current.changeMode('draw_line_string');
    } else if (type === 'point') {
      drawRef.current.changeMode('draw_point');
    }
  };

  const handleSaveFeature = async (metadata) => {
    if (!currentDrawing) {
      alert('No drawing to save');
      return;
    }

    try {
      const featureData = {
        feature_type: drawingType,
        name: metadata.name,
        description: metadata.description,
        geometry: currentDrawing.geometry,
        properties: {
          color: '#667eea',
          ...metadata
        }
      };

      await saveUserFeature(featureData);

      // Reload features
      const data = await fetchUserFeatures();
      setUserFeatures(data.features || []);

      // Clear current drawing
      drawRef.current.deleteAll();
      setCurrentDrawing(null);

      alert('Feature saved successfully!');
    } catch (error) {
      alert(`Failed to save feature: ${error.message}`);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    try {
      await deleteUserFeature(featureId);

      // Reload features
      const data = await fetchUserFeatures();
      setUserFeatures(data.features || []);

      // Remove from map if displayed
      if (mapRef.current.getLayer('user-features-fill')) {
        mapRef.current.getSource('user-features').setData({
          type: 'FeatureCollection',
          features: data.features || []
        });
      }
    } catch (error) {
      alert('Failed to delete feature');
    }
  };

  // Display user features on map
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Wait for map to be fully loaded
    const addUserFeaturesLayers = () => {
      if (!map.isStyleLoaded()) {
        return;
      }

      // Add source if it doesn't exist
      if (!map.getSource('user-features')) {

        map.addSource('user-features', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: userFeatures
          }
        });

        // Add polygon fill layer
        map.addLayer({
          id: 'user-features-fill',
          type: 'fill',
          source: 'user-features',
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: {
            'fill-color': '#667eea',
            'fill-opacity': 0.5
          }
        });

        // Add polygon outline layer
        map.addLayer({
          id: 'user-features-outline',
          type: 'line',
          source: 'user-features',
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: {
            'line-color': '#667eea',
            'line-width': 3
          }
        });

        // Add line layer for roads
        map.addLayer({
          id: 'user-features-line',
          type: 'line',
          source: 'user-features',
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#ff6b35',
            'line-width': 4
          }
        });

        // Add point layer
        map.addLayer({
          id: 'user-features-point',
          type: 'circle',
          source: 'user-features',
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 8,
            'circle-color': '#667eea',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });

      } else {
        // Update existing source
        map.getSource('user-features').setData({
          type: 'FeatureCollection',
          features: userFeatures
        });
      }
    };

    // Handle both initial load and style changes
    if (map.isStyleLoaded()) {
      addUserFeaturesLayers();
    } else {
      // Wait for both 'load' and 'idle' events to ensure style is fully ready
      const onLoad = () => {
        addUserFeaturesLayers();
      };

      map.once('load', onLoad);
      map.once('idle', () => {
        addUserFeaturesLayers();
      });
    }

  }, [userFeatures]);

  // -----------------------------------
  // 🗺️ MAP INITIALIZATION
  // -----------------------------------

  useEffect(() => {
    // Don't initialize map if not authenticated
    if (!authenticated || !initialized) {
      return;
    }

    // Ensure map container exists
    if (!mapContainer.current) {
      return;
    }

    // Ensure PMTiles protocol is registered before creating map
    // if (!protocolRegistered) {
    //   try {
    //     const protocol = new Protocol();
    //     maplibregl.addProtocol("pmtiles", protocol.tile);
    //     protocolRegistered = true;
    //     
    //     // Use CloudFront URL directly for PMTiles (raster tiles)
    //     const pmtilesUrl = "https://map-tiles-dev.pntrzz.com/pmtile/tiles.pmtiles";
    //     
    //     // Fetch PMTiles file header to initialize and verify accessibility
    //     // This will trigger a request to S3
    //     fetch(pmtilesUrl, {
    //       method: 'GET',
    //       headers: {
    //         'Range': 'bytes=0-16383' // Fetch PMTiles header (first 16KB contains metadata)
    //       }
    //     }).then(response => {
    //       if (response.ok || response.status === 206) {
    //         // PMTiles file is accessible
    //       }
    //     }).catch(() => {
    //       // Error will be handled by protocol handler
    //     });
    //   } catch (error) {
    //     // Protocol registration failed
    //   }
    // }

    // Default center (Delhi) - fallback if geolocation fails
    let mapCenter = [77.1277, 28.6304];
    let mapZoom = 14;

    // If permission already granted, wait for geolocation (3s) before creating map.
    // Otherwise, create map immediately with default location, then try geolocation.
    const getUserLocationAndInitMap = async () => {
      // Check if permission is already granted (if Permissions API is available)
      let permissionGranted = false;
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          permissionGranted = permission.state === 'granted';

          // If permission is granted, we can use cached location more aggressively
          if (permissionGranted) {
          }
        }
      } catch (e) {
        // Permissions API not supported or failed, continue with normal flow
      }

      if (!permissionGranted) {
        createMap(mapCenter, mapZoom, null);
      }

      if (!navigator.geolocation) {
        // Geolocation not supported by browser
        return;
      }

      // Request location with appropriate settings
      // If permission already granted, use cached location (up to 5 minutes old)
      // If not granted, still try but with shorter timeout
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLng = position.coords.longitude;
          const userLat = position.coords.latitude;

          if (permissionGranted) {
            // Create map directly at user location when already allowed
            setUserLocation({ lat: userLat, lng: userLng });
            createMap([userLng, userLat], 16, { lng: userLng, lat: userLat });
            return;
          }

          // Otherwise, update map if it already exists
          setUserLocation({ lat: userLat, lng: userLng });
          if (mapRef.current) {
            if (currentLocationMarkerRef.current) {
              currentLocationMarkerRef.current.setLngLat([userLng, userLat]);
            } else {
              const el = document.createElement('div');
              el.className = 'current-location-marker';
              el.innerHTML = `
                <div class="current-location-pulse"></div>
                <div class="current-location-dot"></div>
              `;

              const marker = new maplibregl.Marker({
                element: el,
                anchor: 'center'
              })
                .setLngLat([userLng, userLat])
                .addTo(mapRef.current);

              // Ensure current location pin renders above POI icons
              const locEl = marker.getElement();
              if (locEl) locEl.style.zIndex = '1500';

              currentLocationMarkerRef.current = marker;
            }
            mapRef.current.flyTo({ center: [userLng, userLat], zoom: 16, duration: 1500 });
          }
        },
        (error) => {
          // Could not get user location - keep default location (Delhi)
          if (permissionGranted) {
            createMap(mapCenter, mapZoom, null);
          }
        },
        {
          enableHighAccuracy: true, // Use GPS location (more accurate)
          timeout: 3000, // Cancel if location fetch takes more than 3s
          maximumAge: permissionGranted ? 300000 : 60000 // 5 minutes if granted, 1 minute if not
        }
      );
    };

    // Function to create and initialize the map
    const createMap = (center, zoom, userLocation) => {
      // Function to add current location marker
      const addCurrentLocationMarker = (lng, lat, map) => {
        // Remove existing current location marker if present
        if (currentLocationMarkerRef.current) {
          currentLocationMarkerRef.current.remove();
        }

        // Create custom element for current location marker
        const el = document.createElement('div');
        el.className = 'current-location-marker';
        el.innerHTML = `
          <div class="current-location-pulse"></div>
          <div class="current-location-dot"></div>
        `;

        // Create marker
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'center'
        })
          .setLngLat([lng, lat])
          .addTo(map);

        // Ensure current location pin renders above POI icons
        const locMarkerEl = marker.getElement();
        if (locMarkerEl) locMarkerEl.style.zIndex = '1500';

        currentLocationMarkerRef.current = marker;
      };

      const getGeometryBbox = (geometry) => {
        if (!geometry) return null;
        let minLng = Infinity;
        let minLat = Infinity;
        let maxLng = -Infinity;
        let maxLat = -Infinity;

        const extend = (coord) => {
          const [lng, lat] = coord;
          if (lng < minLng) minLng = lng;
          if (lat < minLat) minLat = lat;
          if (lng > maxLng) maxLng = lng;
          if (lat > maxLat) maxLat = lat;
        };

        const walk = (coords) => {
          if (!Array.isArray(coords)) return;
          if (typeof coords[0] === 'number') {
            extend(coords);
            return;
          }
          coords.forEach(walk);
        };

        walk(geometry.coordinates);

        if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) {
          return null;
        }

        return { minLng, minLat, maxLng, maxLat };
      };

      const defaultMapStyle = {
        version: 8,
        glyphs: "/fonts/{fontstack}/{range}.pbf",
        sprite: "https://maputnik.github.io/osm-liberty/sprites/osm-liberty",
        // Vector source configuration
        sources: {
          // Vector tiles from Tegola (imported from config)
          ...tegolaVectorConfig.source,
          // POI collision placeholder — claims space so road labels yield to POI markers
          "poi-collision-placeholder": {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
          }
        },
        layers: [
          // Background color
          {
            id: "background",
            type: "background",
            paint: {
              "background-color": "#F7F6F6"
            }
          },
          // Vector layers from Tegola (imported from config)
          ...tegolaVectorConfig.layers,
          // Invisible symbol layer that participates in collision detection.
          // Road labels have text-optional:true so they yield to this layer.
          {
            id: "poi-collision-placeholder",
            type: "symbol",
            source: "poi-collision-placeholder",
            layout: {
              "text-field": "██████████████",
              "text-font": ["Noto Sans Regular"],
              "text-size": 16,
              "text-padding": 2,
              "text-allow-overlap": true,
              "text-ignore-placement": false,
              "text-optional": false
            },
            paint: {
              "text-opacity": 0
            }
          }
        ],
      };

      const normalizeMotorwayTrunkRoads = (style) => {
        if (!style || !Array.isArray(style.layers)) return style;

        const widthByLayer = {
          roads_mw_trunk_low_casing: [
            "interpolate", ["linear"], ["zoom"],
            5, 0.26,
            6, 0.51,
            7, 1.1,
            8, 1.1,
            9, 2.1,
            10, 2.1
          ],
          roads_mw_trunk_mid_casing: [
            "interpolate", ["linear"], ["zoom"],
            10, 2.1,
            11, 6,
            12, 6,
            13, 6,
            14, 12,
            15, 14,
            16, 16,
            17, 22,
            18, 32,
            19, 72,
            20, 72
          ],
          roads_mw_trunk_low: [
            "interpolate", ["linear"], ["zoom"],
            5, 0.25,
            6, 0.5,
            7, 1,
            8, 1,
            9, 2,
            10, 2
          ],
          roads_mw_trunk_mid: [
            "interpolate", ["linear"], ["zoom"],
            10, 2,
            11, 4,
            12, 4,
            13, 4,
            14, 10,
            15, 12,
            16, 14,
            17, 20,
            18, 30,
            19, 70,
            20, 70
          ],
          roads_residential_high_casing: [
            "interpolate", ["linear"], ["zoom"],
            14, 0.26,
            15, 1.1,
            16, 2.1,
            17, 2.5,
            18, 14,
            19, 37,
            20, 37
          ],
          roads_residential_high: [
            "interpolate", ["linear"], ["zoom"],
            14, 0.25,
            15, 1,
            16, 2,
            17, 2,
            18, 12,
            19, 35,
            20, 35
          ],
          roads_service_high_casing: [
            "interpolate", ["linear"], ["zoom"],
            17, 2,
            18, 6,
            19, 22,
            20, 22
          ],
          roads_service_high: [
            "interpolate", ["linear"], ["zoom"],
            17, 2,
            18, 6,
            19, 22,
            20, 22
          ]
        };

        return {
          ...style,
          layers: style.layers.map((layer) => {
            if (!widthByLayer[layer.id]) return layer;
            return {
              ...layer,
              paint: {
                ...(layer.paint || {}),
                "line-width": widthByLayer[layer.id],
                "line-opacity": 1
              }
            };
          })
        };
      };

      const savedMapStyle = loadMapStyleFromStorage();
      const mapStyle = savedMapStyle
        ? normalizeMotorwayTrunkRoads(savedMapStyle)
        : defaultMapStyle;

      const map = new maplibregl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: center,
        zoom: zoom,
        minZoom: 4,
        maxZoom: 19.9,
        maxBounds: [
          [44, 1],    // Southwest [lng, lat]
          [110, 38.023432]   // Northeast [lng, lat]
        ],

        tileRequestsDelay: 90, // Debounce tile requests to reduce cancelled calls during zoom/pan

        // Performance optimizations for parallel tile loading
        fadeDuration: 0,  // Instant show/hide for symbols (no fade transitions)
        preserveDrawingBuffer: false,  // Better performance
        refreshExpiredTiles: false,  // Don't reload tiles unnecessarily
        antialias: true,  // Enable anti-aliasing for smoother visuals

        // Parallel tile loading settings
        maxParallelImageRequests: 16,  // Increase for faster loading

        // Render world copies
        renderWorldCopies: false,  // Don't render multiple world copies

        // Optimize for vector tiles
        crossSourceCollisions: false,  // Shields (tegola_transport) don't compete with place labels (tegola)

        // Zoom at cursor position (not center)
        scrollZoom: true,

        // Transform request - proxy handles authentication
        transformRequest: (url, resourceType) => {
          // Handle image loading errors gracefully (for terrain DEM tiles that may not exist)
          if (resourceType === 'Image' && url.includes('.png')) {
            return {
              url: url,
              headers: {},
              collectResourceTiming: false
            };
          }

          return { url };
        }
      });

      mapRef.current = map;

      // Load crosshair SVG icon for place label centroid indicators
      const applyCrosshairIcon = () => {
        const CROSSHAIR_IMAGE_ID = 'crosshair-icon';
        if (map.hasImage(CROSSHAIR_IMAGE_ID)) return;

        const size = 16;
        const pixelRatio = 2;
        const width = size * pixelRatio;
        const height = size * pixelRatio;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const crosshairUrl = `${process.env.PUBLIC_URL || ''}/icons/crosshair_dot.svg`;
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          if (!map.hasImage(CROSSHAIR_IMAGE_ID)) {
            map.addImage(CROSSHAIR_IMAGE_ID, ctx.getImageData(0, 0, width, height), {
              pixelRatio,
              sdf: false
            });
          }
        };
        img.src = crosshairUrl;
      };

      const applyNhShieldYellowBox = () => {
        const nhLayerIds = ['highway_shields_nh_low', 'highway_shields_nh', 'highway_shields_ne'];
        const SMALL_SHIELD_ID = 'nh-shield-small-hidpi-v1';
        const MID_SHIELD_ID = 'nh-shield-mid-hidpi-v1';
        const WIDE_SHIELD_ID = 'nh-shield-wide-hidpi-v1';
        const XWIDE_SHIELD_ID = 'nh-shield-xwide-hidpi-v1';
          try {
            const createShieldImage = ({ imageId, path, logicalWidth, logicalHeight }) => {
              if (map.hasImage(imageId)) return;
            
              const pixelRatio = 4;
              const width = logicalWidth * pixelRatio;
              const height = logicalHeight * pixelRatio;
            
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
            
              const ctx = canvas.getContext('2d');
            
              if (!ctx) return;
            
              ctx.scale(pixelRatio, pixelRatio);
              ctx.translate(1, 1);
            
              const shieldPath = new Path2D(path);
            
              ctx.strokeStyle = '#C6B070';
              ctx.lineWidth = 2.08;
              ctx.lineJoin = 'round';
              ctx.stroke(shieldPath);
            
              ctx.fillStyle = '#FDD835';
              ctx.fill(shieldPath);
            
              ctx.strokeStyle = '#FFFFFF';
              ctx.lineWidth = 0.9;
              ctx.lineJoin = 'round';
              ctx.stroke(shieldPath);
            
              map.addImage(imageId, ctx.getImageData(0, 0, width, height), { pixelRatio });
            };
          
            createShieldImage({
              imageId: SMALL_SHIELD_ID,
              logicalWidth: 20,
              logicalHeight: 20,
              path: 'M1.74219 0.450195H16.2578C16.7672 0.450195 17.0677 0.578477 17.2441 0.757812C17.4219 0.938503 17.5498 1.24789 17.5498 1.77051V11.8037C17.5497 12.3463 17.3218 12.9258 16.793 13.5488C16.6554 13.7109 16.4682 13.8444 16.2461 13.9531L9.93262 17.043C9.3443 17.3309 8.6557 17.3309 8.06738 17.043L1.75391 13.9531C1.5318 13.8444 1.34462 13.7109 1.20703 13.5488C0.678205 12.9258 0.450317 12.3463 0.450195 11.8037V1.77051C0.450195 1.24789 0.578083 0.938503 0.755859 0.757812C0.932308 0.578478 1.23279 0.450195 1.74219 0.450195Z',
            });

            createShieldImage({
              imageId: MID_SHIELD_ID,
              logicalWidth: 28,
              logicalHeight: 22,
              path: 'M22.4121 0.367188C23.2222 0.367227 23.897 0.519813 24.376 0.884766C24.8739 1.26431 25.1016 1.82731 25.1016 2.50488V12.5381C25.1014 13.3989 24.5941 14.1972 23.7266 14.9346C23.4903 15.1354 23.2203 15.2771 22.9482 15.377L13.7471 18.7539C13.0933 18.9938 12.3754 18.9938 11.7217 18.7539L2.52051 15.377C2.24845 15.2771 1.97849 15.1354 1.74219 14.9346C0.874651 14.1972 0.367358 13.3989 0.367188 12.5381V2.50488C0.367188 1.82731 0.594872 1.26431 1.09277 0.884766C1.57175 0.519813 2.2465 0.367227 3.05664 0.367188H22.4121Z',
            });
          
            createShieldImage({
              imageId: WIDE_SHIELD_ID,
              logicalWidth: 32,
              logicalHeight: 22,
              path: 'M26.0244 0.367188C26.9558 0.367188 27.7185 0.516827 28.2578 0.869141C28.8243 1.23929 29.1016 1.80452 29.1016 2.50488V12.5381C29.1014 13.4414 28.4712 14.257 27.4346 14.9971C27.2056 15.1605 26.9536 15.2757 26.7002 15.3555L15.6162 18.8418C15.0421 19.0224 14.4266 19.0224 13.8525 18.8418L2.76855 15.3555C2.51516 15.2757 2.26312 15.1605 2.03418 14.9971C0.997569 14.257 0.367389 13.4414 0.367188 12.5381V2.50488C0.367188 1.80452 0.644431 1.23929 1.21094 0.869141C1.75028 0.516827 2.51294 0.367188 3.44434 0.367188H26.0244Z'
            });

            createShieldImage({
              imageId: XWIDE_SHIELD_ID,
              logicalWidth: 38,
              logicalHeight: 22,
              path: 'M31.4443 0.367188C32.5619 0.367213 33.4575 0.515371 34.0859 0.853516C34.4056 1.02556 34.6638 1.25129 34.8408 1.53711C35.0185 1.82396 35.1016 2.15085 35.1016 2.50488V12.5381C35.1014 13.0202 34.8904 13.4708 34.5312 13.8838C34.174 14.2945 33.655 14.6871 32.9951 15.0664C32.7837 15.1879 32.5588 15.2735 32.333 15.332L18.4717 18.9229C17.9883 19.0481 17.4805 19.0481 16.9971 18.9229L3.13574 15.332C2.9099 15.2735 2.68509 15.1879 2.47363 15.0664C1.81372 14.6871 1.29478 14.2945 0.9375 13.8838C0.578308 13.4708 0.367314 13.0202 0.367188 12.5381V2.50488C0.367188 2.15085 0.450264 1.82396 0.62793 1.53711C0.804989 1.25129 1.06311 1.02556 1.38281 0.853516C2.01123 0.515371 2.90684 0.367213 4.02441 0.367188H31.4443Z'
            });
          
            nhLayerIds.forEach((nhLayerId) => {
              if (!map.getLayer(nhLayerId)) return;
            
              map.setLayoutProperty(nhLayerId, 'visibility', 'visible');
            
              const minZoom = nhLayerId === 'highway_shields_nh_low' ? 7 : 7;
              const maxZoom = nhLayerId === 'highway_shields_nh_low' ? 10 : 20;
            
              map.setLayerZoomRange(nhLayerId, minZoom, maxZoom);
            
              const textField = nhLayerId === 'highway_shields_ne' ? ['get', 'ref'] : ['slice', ['get', 'ref'], 2];
            
              map.setLayoutProperty(nhLayerId, 'text-field', textField);
            
              map.setLayoutProperty(nhLayerId, 'icon-image', ['case', ['<=', ['length', ['to-string', textField]], 2], SMALL_SHIELD_ID, ['==', ['length', ['to-string', textField]], 3], MID_SHIELD_ID, ['>=', ['length', ['to-string', textField]], 5], XWIDE_SHIELD_ID, WIDE_SHIELD_ID]);
            
              map.setLayoutProperty(nhLayerId, 'icon-size', 1.08);
              map.setLayoutProperty(nhLayerId, 'text-font', ['Roboto Medium']);
              map.setLayoutProperty(nhLayerId, 'text-size', 11);
              map.setLayoutProperty(nhLayerId, 'text-line-height', 1.2);
              map.setLayoutProperty(nhLayerId, 'text-letter-spacing', 0);
              map.setLayoutProperty(nhLayerId, 'text-justify', 'center');
              map.setLayoutProperty(nhLayerId, 'text-anchor', 'center');
              map.setLayoutProperty(nhLayerId, 'text-offset', [0, 0]);
              map.setLayoutProperty(nhLayerId, 'symbol-placement', 'point');
              map.setLayoutProperty(nhLayerId, 'symbol-avoid-edges', true);
            
              map.setPaintProperty(nhLayerId, 'icon-opacity', 1);
              map.setPaintProperty(nhLayerId, 'text-color', '#121212');
              map.setPaintProperty(nhLayerId, 'text-opacity', 1);
            });
          } catch (e) {}
      };

      const applyShShieldGreenBox = () => {
        const shLayerId = 'highway_shields_sh';
        const SMALL_SHIELD_ID = 'sh-shield-green-small-hidpi-v1';
        const WIDE_SHIELD_ID = 'sh-shield-green-wide-hidpi-v1';

        try {
          const createShieldImage = ({
            imageId,
            logicalWidth,
            logicalHeight,
            outerPath,
            innerPath,
            outerTranslateX,
            outerTranslateY,
            innerTranslateX,
            innerTranslateY,
          }) => {
            if (map.hasImage(imageId)) return;
          
            const pixelRatio = 4;
            const width = logicalWidth * pixelRatio;
            const height = logicalHeight * pixelRatio;
          
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
          
            const ctx = canvas.getContext('2d');
          
            if (!ctx) return;
          
            ctx.scale(pixelRatio, pixelRatio);
          
            const outer = new Path2D();
            outer.addPath(
              new Path2D(outerPath),
              new DOMMatrix().translate(outerTranslateX, outerTranslateY)
            );
          
            const inner = new Path2D();
            inner.addPath(
              new Path2D(innerPath),
              new DOMMatrix().translate(innerTranslateX, innerTranslateY)
            );
          
            ctx.fillStyle = '#7BB23E';
            ctx.fill(inner);
          
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 0.9;
            ctx.lineJoin = 'round';
            ctx.stroke(inner);
          
            ctx.strokeStyle = '#666666';
            ctx.lineWidth = 0.734694;
            ctx.lineJoin = 'round';
            ctx.stroke(outer);
          
            map.addImage(imageId, ctx.getImageData(0, 0, width, height), { pixelRatio });
          };
        
          createShieldImage({
            imageId: SMALL_SHIELD_ID,
            logicalWidth: 20,
            logicalHeight: 19,
            outerTranslateX: 0.265,
            outerTranslateY: 0.035,
            innerTranslateX: 1,
            innerTranslateY: 0.78,
            outerPath:
              'M16.9922 0.367188C17.6308 0.367188 18.1773 0.529606 18.5605 0.918945C18.9427 1.30741 19.1016 1.85958 19.1016 2.50488V12.5381C19.1014 13.328 18.7651 14.0883 18.1504 14.8125C17.921 15.0828 17.6329 15.2775 17.3398 15.4209L14.417 16.8516C11.4626 18.2974 8.00615 18.2974 5.05176 16.8516L2.12891 15.4209C1.83589 15.2775 1.54779 15.0828 1.31836 14.8125C0.703647 14.0883 0.367311 13.328 0.367188 12.5381V2.50488C0.367188 1.85958 0.526006 1.30741 0.908203 0.918945C1.29147 0.529606 1.83794 0.367188 2.47656 0.367188H16.9922Z',
            innerPath:
              'M1.74219 0.450195H16.2578C16.7672 0.450195 17.0677 0.578477 17.2441 0.757812C17.4219 0.938503 17.5498 1.24789 17.5498 1.77051V11.8037C17.5498 11.9385 17.5363 12.0742 17.5078 12.2109C17.389 12.7821 17.0292 13.2451 16.5068 13.6562C15.9794 14.0714 15.325 14.4041 14.668 14.7256L13.3232 15.3828C10.5955 16.7177 7.40447 16.7177 4.67676 15.3828L2.9375 14.5322C1.87381 14.0117 0.941529 13.4614 0.59668 12.5625C0.497454 12.3037 0.45025 12.0514 0.450195 11.8037V1.77051C0.450195 1.24789 0.578083 0.938503 0.755859 0.757812C0.932308 0.578478 1.23279 0.450195 1.74219 0.450195Z',
          });
        
          createShieldImage({
            imageId: WIDE_SHIELD_ID,
            logicalWidth: 31,
            logicalHeight: 19,
            outerTranslateX: 0.265,
            outerTranslateY: 0.035,
            innerTranslateX: 1,
            innerTranslateY: 0.9,
            outerPath:
              'M26.9277 0.367188C27.8899 0.367188 28.6743 0.51665 29.2285 0.866211C29.8112 1.23384 30.1016 1.79879 30.1016 2.50488V12.5381C30.1014 13.4513 29.44 14.2705 28.3613 15.0107C28.135 15.1661 27.8874 15.275 27.6387 15.3506L18.3301 18.1777C16.3117 18.7907 14.157 18.7907 12.1387 18.1777L2.83008 15.3506C2.58139 15.275 2.33379 15.1661 2.10742 15.0107C1.02878 14.2705 0.367397 13.4513 0.367188 12.5381V2.50488C0.367188 1.79879 0.657502 1.23384 1.24023 0.866211C1.79448 0.51665 2.57889 0.367188 3.54102 0.367188H26.9277Z',
            innerPath:
              'M2.80664 0.450195H26.1934C27.0961 0.450195 27.6968 0.595019 28.0586 0.823242C28.3853 1.02945 28.5498 1.32247 28.5498 1.77051V11.8037C28.5497 12.0889 28.4306 12.4107 28.1201 12.7764C27.4733 13.5381 26.4557 13.8987 25.3408 14.2373L17.3584 16.6611C15.4948 17.2272 13.5052 17.2272 11.6416 16.6611L3.32812 14.1357C2.4544 13.8703 1.6613 13.5615 1.08887 13C0.622441 12.5423 0.450341 12.1449 0.450195 11.8037V1.77051C0.450195 1.32247 0.614682 1.02945 0.941406 0.823242C1.30317 0.595019 1.90395 0.450195 2.80664 0.450195Z',
          });
        
          if (!map.getLayer(shLayerId)) return;
        
          map.setLayoutProperty(shLayerId, 'visibility', 'visible');
          map.setLayoutProperty(shLayerId, 'text-field', ['case', ['==', ['slice', ['get', 'ref'], 2, 3], ' '], ['slice', ['get', 'ref'], 3], ['slice', ['get', 'ref'], 2]]);
          map.setLayoutProperty(shLayerId, 'icon-image', ['case', ['<=', ['length', ['to-string', ['slice', ['get', 'ref'], 2]]], 2], SMALL_SHIELD_ID, WIDE_SHIELD_ID]);
          map.setLayoutProperty(shLayerId, 'icon-size', 1.08);
          map.setLayoutProperty(shLayerId, 'text-font', ['Roboto Medium']);
          map.setLayoutProperty(shLayerId, 'text-size', 11);
          map.setLayoutProperty(shLayerId, 'text-line-height', 1);
          map.setLayoutProperty(shLayerId, 'text-letter-spacing', 0);
          map.setLayoutProperty(shLayerId, 'text-justify', 'center');
          map.setLayoutProperty(shLayerId, 'text-anchor', 'center');
          map.setLayoutProperty(shLayerId,'text-offset',['case',['<=',['length',['to-string',['slice',['get','ref'],2]]],2],['literal',[0,0.02]],['literal',[0,0.04]]]);
          map.setLayoutProperty(shLayerId, 'symbol-placement', 'line');
          map.setLayoutProperty(shLayerId, 'symbol-avoid-edges', true);
        
          map.setPaintProperty(shLayerId, 'icon-opacity', 1);
          map.setPaintProperty(shLayerId, 'text-color', '#121212');
          map.setPaintProperty(shLayerId, 'text-opacity', 1);
          map.setPaintProperty(shLayerId, 'text-halo-width', 0);
        } catch (e) {
          console.error(e);
        }
      };

      const applyMdrShieldPinkBox = () => {
        const mdrLayerIds = [
          'highway_shields_mdr',
          'highway_shields_mdr_secondary',
          'highway_shields_mdr_tertiary'
        ];
        const mdrImageId = 'mdr-shield-white-box-hidpi-v2';

        try {
          if (!map.hasImage(mdrImageId)) {
            const pixelRatio = 4;
            const logicalWidth = 22;
            const logicalHeight = 15;
            const width = logicalWidth * pixelRatio;
            const height = logicalHeight * pixelRatio;
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (ctx) {
              ctx.scale(pixelRatio, pixelRatio);
              const r =4             
              const x = 0.5;
              const y = 0.5;
              const w = logicalWidth - 2;
              const h = logicalHeight - 2;
              // Rounded rectangle path
              ctx.beginPath();
              ctx.moveTo(x + r, y);
              ctx.lineTo(x + w - r, y);
              ctx.arcTo(x + w, y, x + w, y + r, r);
              ctx.lineTo(x + w, y + h - r);
              ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
              ctx.lineTo(x + r, y + h);
              ctx.arcTo(x, y + h, x, y + h - r, r);
              ctx.lineTo(x, y + r);
              ctx.arcTo(x, y, x + r, y, r);
              ctx.closePath();

              ctx.fillStyle = '#FFFFFF';
              ctx.fill();

              
              ctx.strokeStyle = '#454545';
              ctx.lineWidth = 1.2;
              ctx.stroke();

              // Reset shadow
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              map.addImage(mdrImageId, ctx.getImageData(0, 0, width, height), {
              pixelRatio,
              stretchX: [[32, 56]], 
              stretchY: [[24, 36]],
              content: [8, 8, 80, 52]
              });
            }
          }

          mdrLayerIds.forEach(mdrLayerId => {
            if (!map.getLayer(mdrLayerId)) return;

            map.setLayoutProperty(mdrLayerId, 'visibility', 'visible');
            map.setLayerZoomRange(mdrLayerId, 11, 20);
            map.setLayoutProperty(mdrLayerId, 'text-field', [
              'case',
              ['==', ['slice', ['get', 'ref'], 0, 3], 'MDR'],
              ['slice', ['get', 'ref'], 3],
              ['slice', ['get', 'ref'], 2]
            ]);
            map.setLayoutProperty(mdrLayerId, 'icon-image', mdrImageId);
            map.setLayoutProperty(mdrLayerId, 'icon-size', 1.0);
            map.setLayoutProperty(mdrLayerId, 'icon-text-fit', 'both');
            map.setLayoutProperty(mdrLayerId, 'icon-text-fit-padding', [2, 3, 0, 2]);
            map.setLayoutProperty(mdrLayerId, 'text-offset', [0, 0]);
            map.setLayoutProperty(mdrLayerId, 'text-font', ['Roboto Medium']);
            map.setLayoutProperty(mdrLayerId, 'text-size', 10);
            map.setLayoutProperty(mdrLayerId, 'text-line-height', 1.2);
            map.setLayoutProperty(mdrLayerId, 'text-letter-spacing', 0);
            map.setLayoutProperty(mdrLayerId, 'text-justify', 'center');
            map.setPaintProperty(mdrLayerId, 'icon-opacity', 1);
            map.setLayoutProperty(mdrLayerId, 'symbol-placement', 'line');
            map.setLayoutProperty(mdrLayerId, 'symbol-avoid-edges', true);
            map.setPaintProperty(mdrLayerId, 'text-color', '#121212');
            map.setPaintProperty(mdrLayerId, 'text-opacity', 1);
            map.setPaintProperty(mdrLayerId, 'text-halo-color', 'rgba(0,0,0,0)');
            map.setPaintProperty(mdrLayerId, 'text-halo-width', 0);
            map.setPaintProperty(mdrLayerId, 'text-halo-blur', 0);
          });
        } catch (e) {
          // Ignore runtime style errors and continue map init.
        }
      };
      // ========================================================================
      // GOOGLE MAPS-STYLE ZOOM CONFIGURATION
      // ========================================================================

      // Configure smooth scroll zoom (like Google Maps)
      // Slower wheel zoom for smoother, more controlled zooming
      map.scrollZoom.setWheelZoomRate(1 / 200);
      // Trackpad pinch zoom: smooth and controlled
      map.scrollZoom.setZoomRate(1 / 100);

      // Disable default double-click zoom to replace with smooth version
      map.doubleClickZoom.disable();

      // Double-click zoom on canvas with capture phase so it runs before Mapbox Draw
      const handleDblClickZoom = (e) => {
        // Cancel any pending single-click (RVG) so double-click only zooms
        if (pendingMapClickTimeoutRef.current) {
          clearTimeout(pendingMapClickTimeoutRef.current);
          pendingMapClickTimeoutRef.current = null;
        }
        const draw = drawRef.current;
        const mode = draw && draw.getMode ? draw.getMode() : '';
        if (['draw_polygon', 'draw_line_string'].includes(mode)) return; // Let Draw finish shape
        e.preventDefault();
        e.stopPropagation();
        const canvas = map.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
        const currentZoom = map.getZoom();
        map.easeTo({
          zoom: currentZoom + 1,
          duration: 300,
          easing: t => t * (2 - t),
          around: lngLat
        });
      };
      const canvas = map.getCanvas();
      canvas.addEventListener('dblclick', handleDblClickZoom, true);

      // Zoom controls are now handled by the ZoomControls component
      // Old NavigationControl removed

      // Add smooth pinch zoom for mobile (Google Maps style)
      map.touchZoomRotate.disableRotation();  // Disable rotation for cleaner zoom


      // Ensure PMTiles source is loaded and triggers S3 requests
      map.on('load', () => {
        applyCrosshairIcon();
        applyNhShieldYellowBox();
        applyShShieldGreenBox();
        applyMdrShieldPinkBox();

        // DEBUG: Log unique road ref values from all transport layers
        setTimeout(() => {
          const layers = ['roads_mw_trunk_low', 'roads_mw_trunk_mid', 'roads_primary', 'roads_secondary', 'roads_tertiary_mid', 'roads_residential_high'];
          layers.forEach(layer => {
            try {
              const features = map.querySourceFeatures('tegola_transport', { sourceLayer: layer });
              const refs = [...new Set(features.filter(f => f.properties.ref).map(f => f.properties.ref))].sort();
              if (refs.length > 0) {
                console.log(`[DEBUG ROAD REFS] ${layer}:`, refs);
              } else {
                console.log(`[DEBUG ROAD REFS] ${layer}: no ref values found`);
              }
            } catch (e) { /* ignore */ }
          });
        }, 3000);

        // Force the source to load by ensuring it's ready
        // if (map.getSource('osm')) {
        //   // Source exists, trigger tile requests
        //   map.triggerRepaint();
        // }

        // Hover card now works purely on click (no hover setup needed)

        // Add current location marker if we have user location
        if (userLocation) {
          addCurrentLocationMarker(userLocation.lng, userLocation.lat, map);
        }

        // Asia GeoJSON overlay removed - countries now rendered via Tegola tiles

        // -------------------------------------------------------------------
        // Building and POI labels removed as per user request
        // -------------------------------------------------------------------
        const layersToHide = [
          'buildings_labels'
        ];

        layersToHide.forEach(layerId => {
          try {
            if (map.getLayer(layerId)) {
              map.setLayoutProperty(layerId, 'visibility', 'none');
            }
          } catch (e) {
            // ignore
          }
        });

        // -------------------------------------------------------------------
        // Oneway direction arrows on initial load
        // -------------------------------------------------------------------
        if (!map.hasImage('oneway-arrow')) {
          const arrowImg = new Image();
          arrowImg.onload = () => {
            if (!map.hasImage('oneway-arrow')) {
              map.addImage('oneway-arrow', arrowImg, { pixelRatio: 2 });
            }
          };
          arrowImg.src = '/icons/arrow_direction.svg';
        }

        const onewayRoadLayersInit = [
          { sourceLayer: 'roads_motorway', minzoom: 16 },
          { sourceLayer: 'roads_primary', minzoom: 16 },
          { sourceLayer: 'roads_secondary', minzoom: 17, excludeClass: 'secondary_link' },
          { sourceLayer: 'roads_secondary', minzoom: 17.5, classFilter: 'secondary_link', suffix: '_link' },
          { sourceLayer: 'roads_minor', minzoom: 16 },
          { sourceLayer: 'roads_service', minzoom: 16 },
          { sourceLayer: 'roads_path', minzoom: 16 }
        ];

        onewayRoadLayersInit.forEach(({ sourceLayer, minzoom, classFilter, excludeClass, suffix }) => {
          const layerId = `${sourceLayer}_oneway_arrows${suffix || ''}`;
          if (!map.getLayer(layerId)) {
            const onewayFilter = ['any',
              ['==', ['get', 'oneway'], true],
              ['==', ['get', 'oneway'], 'yes'],
              ['==', ['get', 'oneway'], 1]
            ];
            let filter;
            if (classFilter) {
              filter = ['all', onewayFilter, ['==', ['get', 'class'], classFilter]];
            } else if (excludeClass) {
              filter = ['all', onewayFilter, ['!=', ['get', 'class'], excludeClass]];
            } else {
              filter = onewayFilter;
            }
            // Add before road labels so labels get collision priority over arrows
            const beforeLayer = map.getLayer('roads_motorway_labels') ? 'roads_motorway_labels' : undefined;
            map.addLayer({
              id: layerId,
              type: 'symbol',
              source: 'tegola',
              'source-layer': sourceLayer,
              minzoom: minzoom,
              maxzoom: 20,
              filter: filter,
              layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 200,
                'icon-image': 'oneway-arrow',
                'icon-size': [
                  'interpolate', ['linear'], ['zoom'],
                  12, 0.9, 16, 1.35, 20, 1.8
                ],
                'icon-rotation-alignment': 'map',
                'icon-allow-overlap': false,
                'icon-ignore-placement': false,
                'icon-padding': 2
              },
              paint: {
                'icon-opacity': 1,
                'icon-opacity-transition': { duration: 0, delay: 0 }
              }
            }, beforeLayer);
          }
        });

        // Keep POI layers renderable but invisible so queryRenderedFeatures works during movement
        const poiLayers = [
          'pois_important',
          'pois_amenity',
          'pois_shop_tourism_leisure',
          'pois_natural_manmade'
        ];

        poiLayers.forEach(layerId => {
          try {
            if (map.getLayer(layerId)) {
              // POI layers are circle type with zero opacity — ensure they stay visible for querying
              map.setLayoutProperty(layerId, 'visibility', 'visible');
            }
          } catch (e) {
            // ignore
          }
        });

        // Load bus stop icon for the native symbol layer
        const busStopImg = new Image();
        busStopImg.onload = () => {
          if (!map.hasImage('bus-stop-icon')) {
            map.addImage('bus-stop-icon', busStopImg, { pixelRatio: 40 / 14 });
          }
        };
        busStopImg.src = '/icons/bust_stop_poi.png';

        // Load metro station icon for the native symbol layer
        const metroStationImg = new Image();
        metroStationImg.onload = () => {
          if (!map.hasImage('metro-station-icon')) {
            map.addImage('metro-station-icon', metroStationImg, { pixelRatio: 40 / 14 });
          }
        };
        metroStationImg.src = '/icons/metro_station_poi.png';

        // Load airport pin icon for the aeroway_polygon_labels symbol layer
        const airportPinImgInit = new Image();
        airportPinImgInit.onload = () => {
          if (!map.hasImage('airport-pin-icon')) {
            map.addImage('airport-pin-icon', airportPinImgInit, { pixelRatio: 2 });
          }
        };
        airportPinImgInit.src = '/icons/airport_pin.png';

        // Load railway station icon for the railway_stations_dedupe_labels symbol layer
        const railwayStationImgInit = new Image();
        railwayStationImgInit.onload = () => {
          if (!map.hasImage('railway-station-icon')) {
            map.addImage('railway-station-icon', railwayStationImgInit, { pixelRatio: 2 });
          }
        };
        railwayStationImgInit.src = '/icons/railway_Station_poi.png';

        // -------------------------------------------------------------------
        // Deduplicate lake labels so a single lake name shows once per frame
        // -------------------------------------------------------------------
        try {
          if (map.getLayer('water_lakes_labels')) {
            map.setLayoutProperty('water_lakes_labels', 'visibility', 'none');
          }
          if (map.getLayer('water_polygon_high_labels')) {
            map.setLayoutProperty('water_polygon_high_labels', 'visibility', 'none');
          }
        } catch (e) {
          // ignore
        }

        if (!map.getSource('water_lakes_dedupe')) {
          map.addSource('water_lakes_dedupe', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
        }

        if (!map.getLayer('water_lakes_labels_dedupe')) {
          const lakeLabelLayer = {
            id: 'water_lakes_labels_dedupe',
            type: 'symbol',
            source: 'water_lakes_dedupe',
            minzoom: 13.5,
            maxzoom: 20,
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Roboto Regular"],
              "text-size": [
                "interpolate", ["linear"], ["zoom"],
                8, 10,
                12.5, 14,
                16, 16
              ],
              "text-line-height": 1.43,
              "text-letter-spacing": 0.018,
              "text-padding": 8,
              "text-allow-overlap": false,
              "text-ignore-placement": false
            },
            paint: {
              "text-color": "#00879D",
              "text-halo-color": "#FFFFFF",
              "text-halo-width": 0,
              "text-halo-blur": 0,
              "text-opacity": [
                "interpolate", ["linear"], ["zoom"],
                13.5, 1
              ]
            }
          };

          try {
            if (map.getLayer('water_lakes_labels')) {
              map.addLayer(lakeLabelLayer, 'water_lakes_labels');
            } else {
              map.addLayer(lakeLabelLayer);
            }
          } catch (e) {
            // ignore
          }
        }

        const updateLakeLabels = () => {
          const source = map.getSource('water_lakes_dedupe');
          if (!source) return;

          const features = map.querySourceFeatures('tegola', {
            sourceLayer: 'water_lakes'
          });

          // Also query water_polygon_high for named water bodies (e.g. Najafgarh Jheel)
          const waterPolyFeatures = map.querySourceFeatures('tegola', {
            sourceLayer: 'water_polygon_high'
          });
          features.push(...waterPolyFeatures);

          const byName = new Map();

          features.forEach((feature) => {
            const name = feature?.properties?.name;
            if (!name) return;

            const bbox = getGeometryBbox(feature.geometry);
            if (!bbox) return;

            const existing = byName.get(name);
            if (!existing) {
              // First polygon with this name — use its bbox as the combined bbox
              byName.set(name, {
                name,
                minLng: bbox.minLng,
                minLat: bbox.minLat,
                maxLng: bbox.maxLng,
                maxLat: bbox.maxLat
              });
            } else {
              // Expand the combined bbox to include this polygon
              existing.minLng = Math.min(existing.minLng, bbox.minLng);
              existing.minLat = Math.min(existing.minLat, bbox.minLat);
              existing.maxLng = Math.max(existing.maxLng, bbox.maxLng);
              existing.maxLat = Math.max(existing.maxLat, bbox.maxLat);
            }
          });

          const deduped = {
            type: 'FeatureCollection',
            features: Array.from(byName.values()).map((item) => ({
              type: 'Feature',
              properties: { name: item.name },
              geometry: {
                type: 'Point',
                coordinates: [
                  (item.minLng + item.maxLng) / 2,
                  (item.minLat + item.maxLat) / 2
                ]
              }
            }))
          };

          source.setData(deduped);
        };

        updateLakeLabels();
        map.on('moveend', updateLakeLabels);
        map.on('zoomend', updateLakeLabels);

        // -------------------------------------------------------------------
        // Deduplicate airport labels so each airport shows a single icon at
        // its centroid rather than one per polygon feature.
        // -------------------------------------------------------------------
        if (!map.getSource('airports_dedupe')) {
          map.addSource('airports_dedupe', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
        }

        if (!map.getLayer('airports_dedupe_labels')) {
          map.addLayer({
            id: 'airports_dedupe_labels',
            type: 'symbol',
            source: 'airports_dedupe',
            minzoom: 10,
            maxzoom: 20,
            layout: {
              "icon-image": "airport-pin-icon",
              "icon-size": [
                "interpolate", ["linear"], ["zoom"],
                10, 0.6, 13, 0.8, 16, 1
              ],
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
              "text-field": ["get", "name"],
              "text-font": ["Roboto Medium"],
              "text-size": 12,
              "text-anchor": "right",
              "text-offset": [-1.2, -1],
              "text-max-width": 8,
              "text-padding": 2,
              "text-allow-overlap": false,
              "text-optional": true
            },
            paint: {
              "icon-opacity": 1,
              "text-color": "#4CAFBF",
              "text-halo-color": "#FFFFFF",
              "text-halo-width": 0.5,
              "text-halo-blur": 0,
              "text-opacity": 1
            }
          });
        }

        const updateAirportLabels = () => {
          const source = map.getSource('airports_dedupe');
          if (!source) return;

          const features = map.querySourceFeatures('tegola', {
            sourceLayer: 'aeroway_polygon',
            filter: ['all', ['==', ['get', 'class'], 'aerodrome'], ['!=', ['get', 'name'], '']]
          });

          // Group polygons by name and compute a combined bounding box
          const byName = new Map();
          features.forEach((feature) => {
            const name = feature?.properties?.name;
            if (!name) return;

            const bbox = getGeometryBbox(feature.geometry);
            if (!bbox) return;

            const existing = byName.get(name);
            if (!existing) {
              byName.set(name, {
                name,
                minLng: bbox.minLng,
                minLat: bbox.minLat,
                maxLng: bbox.maxLng,
                maxLat: bbox.maxLat
              });
            } else {
              existing.minLng = Math.min(existing.minLng, bbox.minLng);
              existing.minLat = Math.min(existing.minLat, bbox.minLat);
              existing.maxLng = Math.max(existing.maxLng, bbox.maxLng);
              existing.maxLat = Math.max(existing.maxLat, bbox.maxLat);
            }
          });

          const deduped = {
            type: 'FeatureCollection',
            features: Array.from(byName.values()).map((item) => ({
              type: 'Feature',
              properties: { name: item.name },
              geometry: {
                type: 'Point',
                coordinates: [
                  (item.minLng + item.maxLng) / 2,
                  (item.minLat + item.maxLat) / 2
                ]
              }
            }))
          };

          source.setData(deduped);
        };

        updateAirportLabels();
        map.on('moveend', updateAirportLabels);
        map.on('zoomend', updateAirportLabels);

        // -------------------------------------------------------------------
        // Deduplicate railway station labels so each station shows a single
        // icon at its centroid rather than one per polygon feature.
        // -------------------------------------------------------------------
        if (!map.getSource('railway_stations_dedupe')) {
          map.addSource('railway_stations_dedupe', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
        }

        if (!map.getLayer('railway_stations_dedupe_labels')) {
          map.addLayer({
            id: 'railway_stations_dedupe_labels',
            type: 'symbol',
            source: 'railway_stations_dedupe',
            minzoom: 10,
            maxzoom: 20,
            layout: {
              "icon-image": "railway-station-icon",
              "icon-size": [
                "interpolate", ["linear"], ["zoom"],
                10, 0.6, 13, 0.8, 16, 1
              ],
              "icon-allow-overlap": false,
              "icon-ignore-placement": false,
              "icon-anchor": "bottom",
              "icon-optional": false,
              "text-field": ["get", "name"],
              "text-font": ["Roboto Medium"],
              "text-size": 12,
              "text-anchor": "right",
              "text-offset": [-1.2, -1],
              "text-max-width": 8,
              "text-padding": 2,
              "text-allow-overlap": false,
              "text-optional": true,
              "symbol-sort-key": 1
            },
            paint: {
              "icon-opacity": 1,
              "text-color": "#5B80F7",
              "text-halo-color": "#FFFFFF",
              "text-halo-width": 0.5,
              "text-halo-blur": 0,
              "text-opacity": 1
            }
          });
        }

        // Persistent cache of railway station bounding boxes so centroids
        // don't shift as different tile fragments become visible on zoom.
        const railwayStationCache = new Map();

        const updateRailwayStationLabels = () => {
          const source = map.getSource('railway_stations_dedupe');
          if (!source) return;

          const features = map.querySourceFeatures('tegola', {
            sourceLayer: 'landuse_developed_high',
            filter: ['all', ['==', ['get', 'class'], 'railway'], ['!=', ['get', 'name'], '']]
          });

          // Expand cached bounding boxes with any new geometry data
          features.forEach((feature) => {
            const name = feature?.properties?.name;
            if (!name) return;

            const bbox = getGeometryBbox(feature.geometry);
            if (!bbox) return;

            const existing = railwayStationCache.get(name);
            if (!existing) {
              railwayStationCache.set(name, {
                name,
                minLng: bbox.minLng,
                minLat: bbox.minLat,
                maxLng: bbox.maxLng,
                maxLat: bbox.maxLat
              });
            } else {
              existing.minLng = Math.min(existing.minLng, bbox.minLng);
              existing.minLat = Math.min(existing.minLat, bbox.minLat);
              existing.maxLng = Math.max(existing.maxLng, bbox.maxLng);
              existing.maxLat = Math.max(existing.maxLat, bbox.maxLat);
            }
          });

          const deduped = {
            type: 'FeatureCollection',
            features: Array.from(railwayStationCache.values()).map((item) => ({
              type: 'Feature',
              properties: { name: item.name },
              geometry: {
                type: 'Point',
                coordinates: [
                  (item.minLng + item.maxLng) / 2,
                  (item.minLat + item.maxLat) / 2
                ]
              }
            }))
          };

          source.setData(deduped);
        };

        updateRailwayStationLabels();
        map.on('moveend', updateRailwayStationLabels);
        map.on('zoomend', updateRailwayStationLabels);
      });

      // Explicitly load PMTiles file when style is loaded
      map.on('style.load', () => {
        applyCrosshairIcon();
        applyNhShieldYellowBox();
        applyShShieldGreenBox();
        applyMdrShieldPinkBox();

        // Load bus stop icon and add layer dynamically
        const busStopImgReload = new Image();
        busStopImgReload.onload = () => {
          if (!map.hasImage('bus-stop-icon')) {
            map.addImage('bus-stop-icon', busStopImgReload, { pixelRatio: 40 / 14 });
          }
          if (!map.getLayer('bus_stops')) {
            map.addLayer({
              id: "bus_stops",
              type: "symbol",
              source: "tegola",
              "source-layer": "bus_stops",
              minzoom: 17,
              maxzoom: 20,
              layout: {
                "icon-image": "bus-stop-icon",
                "icon-size": 1,
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
                "icon-padding": 0,
                "text-field": ["get", "name"],
                "text-font": ["Noto Sans Regular"],
                "text-size": 10,
                "text-anchor": "left",
                "text-offset": [1, 0],
                "text-max-width": 8,
                "text-allow-overlap": false,
                "text-optional": true
              },
              paint: {
                "icon-opacity": 1,
                "text-color": "#555",
                "text-halo-color": "#fff",
                "text-halo-width": 1,
                "text-opacity": ["step", ["zoom"], 0, 18, 1]
              }
            });
          }
        };
        busStopImgReload.src = '/icons/bust_stop_poi.png';

        // Load metro station icon and add layer dynamically
        const metroStationImgReload = new Image();
        metroStationImgReload.onload = () => {
          if (!map.hasImage('metro-station-icon')) {
            map.addImage('metro-station-icon', metroStationImgReload, { pixelRatio: 40 / 14 });
          }
          if (!map.getLayer('metro_stations')) {
            map.addLayer({
              id: "metro_stations",
              type: "symbol",
              source: "tegola",
              "source-layer": "pois_transport",
              minzoom: 13,
              maxzoom: 20,
              filter: ["==", ["get", "class"], "metro_station"],
              layout: {
                "icon-image": "metro-station-icon",
                "icon-size": 1,
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
                "icon-padding": 0,
                "text-field": ["get", "name"],
                "text-font": ["Noto Sans Regular"],
                "text-size": 10,
                "text-anchor": "left",
                "text-offset": [1, 0],
                "text-max-width": 8,
                "text-allow-overlap": false,
                "text-optional": true
              },
              paint: {
                "icon-opacity": 1,
                "text-color": "#555",
                "text-halo-color": "#fff",
                "text-halo-width": 1,
                "text-opacity": 1
              }
            });
          }
        };
        metroStationImgReload.src = '/icons/metro_station_poi.png';

        // Load airport pin icon for the aeroway_polygon_labels layer
        const airportPinImg = new Image();
        airportPinImg.onload = () => {
          if (!map.hasImage('airport-pin-icon')) {
            map.addImage('airport-pin-icon', airportPinImg, { pixelRatio: 2 });
          }
        };
        airportPinImg.src = '/icons/airport_pin.png';

        // Load railway station icon on style reload
        const railwayStationImgReload = new Image();
        railwayStationImgReload.onload = () => {
          if (!map.hasImage('railway-station-icon')) {
            map.addImage('railway-station-icon', railwayStationImgReload, { pixelRatio: 2 });
          }
        };
        railwayStationImgReload.src = '/icons/railway_Station_poi.png';

        // -------------------------------------------------------------------
        // Oneway direction arrows — load icon and add symbol layers for
        // each road source-layer filtered on oneway=true.
        // -------------------------------------------------------------------
        if (!map.hasImage('oneway-arrow')) {
          const arrowImgReload = new Image();
          arrowImgReload.onload = () => {
            if (!map.hasImage('oneway-arrow')) {
              map.addImage('oneway-arrow', arrowImgReload, { pixelRatio: 2 });
            }
          };
          arrowImgReload.src = '/icons/arrow_direction.svg';
        }

        // Add oneway arrow layers for all road source-layers
        const onewayRoadLayers = [
          { sourceLayer: 'roads_motorway', minzoom: 16 },
          { sourceLayer: 'roads_primary', minzoom: 16 },
          { sourceLayer: 'roads_secondary', minzoom: 17, excludeClass: 'secondary_link' },
          { sourceLayer: 'roads_secondary', minzoom: 17.5, classFilter: 'secondary_link', suffix: '_link' },
          { sourceLayer: 'roads_minor', minzoom: 16 },
          { sourceLayer: 'roads_service', minzoom: 16 },
          { sourceLayer: 'roads_path', minzoom: 16 }
        ];

        onewayRoadLayers.forEach(({ sourceLayer, minzoom, classFilter, excludeClass, suffix }) => {
          const layerId = `${sourceLayer}_oneway_arrows${suffix || ''}`;
          if (!map.getLayer(layerId)) {
            const onewayFilter = ['any',
              ['==', ['get', 'oneway'], true],
              ['==', ['get', 'oneway'], 'yes'],
              ['==', ['get', 'oneway'], 1]
            ];
            let filter;
            if (classFilter) {
              filter = ['all', onewayFilter, ['==', ['get', 'class'], classFilter]];
            } else if (excludeClass) {
              filter = ['all', onewayFilter, ['!=', ['get', 'class'], excludeClass]];
            } else {
              filter = onewayFilter;
            }
            // Add before road labels so labels get collision priority over arrows
            const beforeLayer = map.getLayer('roads_motorway_labels') ? 'roads_motorway_labels' : undefined;
            map.addLayer({
              id: layerId,
              type: 'symbol',
              source: 'tegola',
              'source-layer': sourceLayer,
              minzoom: minzoom,
              maxzoom: 20,
              filter: filter,
              layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 200,
                'icon-image': 'oneway-arrow',
                'icon-size': [
                  'interpolate', ['linear'], ['zoom'],
                  12, 0.9, 16, 1.35, 20, 1.8
                ],
                'icon-rotation-alignment': 'map',
                'icon-allow-overlap': false,
                'icon-ignore-placement': false,
                'icon-padding': 2
              },
              paint: {
                'icon-opacity': 1,
                'icon-opacity-transition': { duration: 0, delay: 0 }
              }
            }, beforeLayer);
          }
        });

        // const pmtilesUrl = "https://map-tiles-dev.pntrzz.com/pmtile/tiles.pmtiles";
        // 
        // // Pre-fetch PMTiles file header to ensure it's accessible
        // // This will trigger a request to S3
        // fetch(pmtilesUrl, { 
        //   method: 'HEAD',
        //   headers: {
        //     'Range': 'bytes=0-16383' // Fetch PMTiles header (first 16KB)
        //   }
        // }).catch(() => {
        //   // Silently handle errors - protocol handler will manage retries
        // });

        // Ensure the source triggers tile loading
        // if (map.getSource('osm')) {
        //   // Force map to request tiles
        //   map.triggerRepaint();
        // }
      });

      // Track zoom level changes
      map.on('zoom', () => {
        setCurrentZoom(map.getZoom());
      });

      // Set initial zoom
      setCurrentZoom(map.getZoom());

      // 3D helpers
      const enable3D = () => {
        if (!map.isStyleLoaded || !map.isStyleLoaded()) {
          return;
        }
        try {
          if (!map.getSource('terrain-dem')) {
            map.addSource('terrain-dem', {
              type: 'raster-dem',
              url: 'https://demotiles.maplibre.org/terrain-tiles/tiles.json',
              tileSize: 256,
              maxzoom: 14
            });
          }
          map.setTerrain({ source: 'terrain-dem', exaggeration: 1.2 });

          if (!map.getLayer('sky')) {
            map.addLayer({
              id: 'sky',
              type: 'sky',
              paint: {
                'sky-type': 'atmosphere',
                'sky-atmosphere-sun': [0.0, 0.0],
                'sky-atmosphere-sun-intensity': 15
              }
            });
          }

          const extrusionSettings = map._building3DSettings || DEFAULT_BUILDING_3D_SETTINGS;
          const effectiveStartZoom = Math.max(extrusionSettings.startZoom, MIN_BUILDINGS_VISIBLE_ZOOM);
          if (!map.getLayer('buildings_extrusion')) {
            map.addLayer({
              id: 'buildings_extrusion',
              type: 'fill-extrusion',
              source: 'tegola',
              'source-layer': 'buildings',
              minzoom: effectiveStartZoom,
              paint: {
                'fill-extrusion-color': extrusionSettings.sideColor,
                'fill-extrusion-height': [
                  'case',
                  ['has', 'height'], ['to-number', ['get', 'height']],
                  ['has', 'levels'], ['*', ['to-number', ['get', 'levels']], 3.5],
                  12
                ],
                'fill-extrusion-opacity': 0.9
              }
            });
          } else {
            map.setPaintProperty('buildings_extrusion', 'fill-extrusion-color', extrusionSettings.sideColor);
            map.setLayerZoomRange('buildings_extrusion', effectiveStartZoom, 24);
          }

          map.easeTo({ pitch: 60, bearing: 30, duration: 800 });
        } catch (err) {
          // Error enabling 3D
        }
      };

      const disable3D = () => {
        if (!map.isStyleLoaded || !map.isStyleLoaded()) {
          return;
        }
        try {
          map.setTerrain(null);
          if (map.getLayer('sky')) map.removeLayer('sky');
          if (map.getLayer('buildings_extrusion')) map.removeLayer('buildings_extrusion');
          if (map.getSource('terrain-dem')) map.removeSource('terrain-dem');
          map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
        } catch (err) {
          // Error disabling 3D
        }
      };

      // 3D functions are now handled by the ZoomControls component
      // Store the functions in the map instance for access from the component
      map._building3DSettings = loadBuilding3DSettings();
      map._enable3D = enable3D;
      map._disable3D = disable3D;
      map._applyBuilding3DSettings = (settings) => {
        map._building3DSettings = settings;
        const effectiveStartZoom = Math.max(settings.startZoom, MIN_BUILDINGS_VISIBLE_ZOOM);
        try {
          const style = map.getStyle && map.getStyle();
          const buildingLayerIds = Array.isArray(style?.layers)
            ? style.layers
              .filter((layer) => layer && layer.id && layer['source-layer'] === 'buildings' && layer.type !== 'fill-extrusion')
              .map((layer) => layer.id)
            : [];

          buildingLayerIds.forEach((layerId) => {
            const layerDef = map.getLayer(layerId);
            if (!layerDef) return;
            map.setLayerZoomRange(layerId, effectiveStartZoom, 20);
            if (layerDef.type === 'fill') {
              map.setPaintProperty(layerId, 'fill-opacity', 1);
            } else if (layerDef.type === 'line') {
              map.setPaintProperty(layerId, 'line-opacity', 1);
            } else if (layerDef.type === 'symbol') {
              map.setPaintProperty(layerId, 'text-opacity', 1);
            }
          });

          if (map.getLayer('buildings_extrusion')) {
            map.setPaintProperty('buildings_extrusion', 'fill-extrusion-color', settings.sideColor);
            map.setLayerZoomRange('buildings_extrusion', effectiveStartZoom, 24);
          }
          if (map.getLayer('buildings')) {
            map.setPaintProperty('buildings', 'fill-color', [
              'case',
              ['==', ['get', 'building_type'], 'commercial building'],
              '#FFF2DA',
              '#E7E8EB'
            ]);
            map.setLayerZoomRange('buildings', effectiveStartZoom, 20);
          }
          if (map.getLayer('buildings_outline')) {
            map.setLayerZoomRange('buildings_outline', effectiveStartZoom, 20);
          }
          map.triggerRepaint();
        } catch (err) {
          // Ignore runtime map style errors.
        }
      };
      map._applyBuilding3DSettings(map._building3DSettings);

      // Suppress image loading errors for missing tiles (terrain DEM tiles may not exist for all zoom levels)
      map.on('error', (e) => {
        if (e.error) {
          const errorMsg = e.error.message ? e.error.message.toLowerCase() : '';
          // Silently handle missing tile images (common with terrain DEM at high zoom levels)
          if (errorMsg.includes('could not load image') ||
            errorMsg.includes('failed to load') ||
            (e.error.status && e.error.status === 404)) {
            // Prevent error from propagating
            e.preventDefault && e.preventDefault();
            return;
          }
        }
      });

      return () => {
        canvas.removeEventListener('dblclick', handleDblClickZoom, true);
        // Remove 3D artifacts on unmount
        if (map && map.isStyleLoaded && map.isStyleLoaded()) {
          try {
            map.setTerrain(null);
            if (map.getLayer && map.getLayer('sky')) map.removeLayer('sky');
            if (map.getLayer && map.getLayer('buildings_extrusion')) map.removeLayer('buildings_extrusion');
            if (map.getSource && map.getSource('terrain-dem')) map.removeSource('terrain-dem');
          } catch (err) {
            // Silently handle cleanup errors
          }
        }
        // Remove current location marker
        if (currentLocationMarkerRef.current) {
          currentLocationMarkerRef.current.remove();
          currentLocationMarkerRef.current = null;
        }

        // Remove search result marker
        if (searchResultMarkerRef.current) {
          searchResultMarkerRef.current.remove();
          searchResultMarkerRef.current = null;
        }

        // Remove map
        if (map) {
          map.remove();
        }
      };
    }; // End of createMap function

    // Start the process: create map, then try to get location
    getUserLocationAndInitMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, initialized]);

  // Keep current location marker visible unless a route point is the current location
  useEffect(() => {
    if (!mapRef.current || !currentLocationMarkerRef.current || !userLocation) {
      return;
    }

    const getPointLng = (point) => {
      if (!point) return null;
      if (typeof point.lng === 'number') return point.lng;
      if (typeof point.lon === 'number') return point.lon;
      if (typeof point.long === 'number') return point.long;
      return null;
    };

    const isUserLocationPoint = (point) => {
      if (!point || typeof point.lat !== 'number') return false;
      const pointLng = getPointLng(point);
      if (typeof pointLng !== 'number') return false;
      return (
        Math.abs(point.lat - userLocation.lat) < 1e-5 &&
        Math.abs(pointLng - userLocation.lng) < 1e-5
      );
    };

    const shouldHide =
      isUserLocationPoint(routing.routePointA) ||
      isUserLocationPoint(routing.routePointB);

    const marker = currentLocationMarkerRef.current;
    if (shouldHide) {
      if (marker._map) {
        marker.remove();
      }
    } else if (!marker._map) {
      marker.addTo(mapRef.current);
    }
  }, [routing.routePointA, routing.routePointB, userLocation]);

  // -----------------------------------
  // 🖱️ CURSOR CHANGE FOR RVG MODE
  // -----------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const canvas = map.getCanvas();
    if (isRvgMode) {
      canvas.style.cursor = 'pointer';
    } else {
      canvas.style.cursor = '';
    }
  }, [isRvgMode]);

  const clearRvgUi = useCallback(() => {
    poi.resetSelectedPoiMarker?.();
    reverseGeocode.setShowPopup(false);
    reverseGeocode.setPopupData(null);
    reverseGeocode.setPopupPosition(null);
    reverseGeocode.clearRvgPolygon();
    reverseGeocode.clearRvgMarkers();
    reverseGeocode.setRvgResults([]);
    reverseGeocode.setShowSidebar(false);
    reverseGeocode.setHighlightedResultIndex(null);
    if (searchResultMarkerRef.current) {
      searchResultMarkerRef.current.remove();
      searchResultMarkerRef.current = null;
    }
    lastPinnedLocationRef.current = null;
  }, [poi, reverseGeocode]);

  const clearSearchBar = useCallback(() => {
    setSearchClearSignal((prev) => prev + 1);
  }, []);

  const getUserLocationDetails = useCallback((location) => {
    if (!location) {
      return null;
    }
    const label = 'Your Location';
    return {
      entity_name: label,
      display_name: label,
      name: label,
      rooftop_name: label,
      location: {
        lat: location.lat,
        lon: location.lng,
        long: location.lng
      }
    };
  }, []);

  // Handle route selection - swap selected route with recommended route on map
  const handleSelectRoute = useCallback((index) => {
    // Skip if same route is already selected
    if (index === selectedRouteIndex) {
      return;
    }
    
    setSelectedRouteIndex(index);
    
    // If we have route details and alternate routes, redraw the map with swapped styling
    if (routing.routeDetails && mapRef.current) {
      const { geometry, duration, distance, alternateRoutes = [] } = routing.routeDetails;
      
      // Safety check - ensure we have valid geometry
      if (!geometry || !geometry.coordinates) {
        console.warn('No valid geometry in routeDetails');
        return;
      }
      
      if (index === 0) {
        // Selected the recommended route - draw normally with original data
        // Create index map: visual index -> logical index (1, 2, 3, ...)
        const indexMap = alternateRoutes.map((_, i) => i + 1);
        routing.drawRouteOnMap(
          geometry,
          routing.routePointA,
          routing.routePointB,
          false, // Don't fit bounds on selection change
          duration,
          distance,
          routeStopsRef.current.length > 0 ? routeStopsRef.current[routeStopsRef.current.length - 1] : null,
          routing.travelMode,
          alternateRoutes,
          indexMap,
          routeStopsRef.current
        );
      } else {
        // Selected an alternate route - swap visuals on map only
        const selectedAltIndex = index - 1;
        const selectedAlt = alternateRoutes[selectedAltIndex];
        
        if (selectedAlt && selectedAlt.geometry && selectedAlt.geometry.coordinates) {
          // Create new alternate routes array with the original recommended route swapped in
          // Also create index map to track which logical route is at each visual position
          const newAlternates = [];
          const indexMap = [];
          
          alternateRoutes.forEach((alt, i) => {
            if (i === selectedAltIndex) {
              // Replace the selected alternate with the original recommended route
              newAlternates.push({
                geometry: geometry,
                duration: duration,
                distance: distance,
                summary: routing.routeDetails.summary
              });
              indexMap.push(0); // This visual alternate is actually the original route (index 0)
            } else {
              newAlternates.push(alt);
              indexMap.push(i + 1); // Original alternate indices
            }
          });
          
          // Draw with the selected alternate as the main route
          routing.drawRouteOnMap(
            selectedAlt.geometry,
            routing.routePointA,
            routing.routePointB,
            false, // Don't fit bounds on selection change
            selectedAlt.duration,
            selectedAlt.distance,
            routeStopsRef.current.length > 0 ? routeStopsRef.current[routeStopsRef.current.length - 1] : null,
            routing.travelMode,
            newAlternates,
            indexMap,
            routeStopsRef.current
          );
        }
      }
    }
  }, [routing, selectedRouteIndex]);

  // Set up the alternate route click handler
  useEffect(() => {
    routing.alternateRouteClickHandlerRef.current = handleSelectRoute;
  }, [handleSelectRoute, routing.alternateRouteClickHandlerRef]);

  const handleRoutePointsChange = useCallback(async (point, coords, details) => {
    const resolveLabel = (data, fallback) => {
      return data?.entity_name || data?.display_name || data?.rooftop_name || data?.name || fallback;
    };

    const formatShortCoordinates = (lat, lng, precision = 4) => {
      const safeLat = Number(lat);
      const safeLng = Number(lng);
      if (!Number.isFinite(safeLat) || !Number.isFinite(safeLng)) {
        return '';
      }
      return `${safeLat.toFixed(precision)}, ${safeLng.toFixed(precision)}`;
    };

    const isCoordinateLabel = (label) => {
      if (!label) return false;
      const normalized = String(label).trim();
      if (/^coordinates:/i.test(normalized)) return true;
      return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(normalized);
    };

    const ensureReadableRouteLabel = async (coordsToUse, detailsToUse, fallbackLabel) => {
      const baseLabel = resolveLabel(detailsToUse, fallbackLabel);
      if (!isCoordinateLabel(baseLabel) || baseLabel === 'Your Location') {
        return { details: detailsToUse, label: baseLabel };
      }

      // Raw coordinates — just use them directly, no RVG call needed
      const shortCoords = formatShortCoordinates(coordsToUse.lat, coordsToUse.lng);
      const fallbackCoordsLabel = shortCoords || fallbackLabel;
      const nextDetails = {
        ...detailsToUse,
        entity_name: detailsToUse?.entity_name || fallbackCoordsLabel,
        display_name: detailsToUse?.display_name || fallbackCoordsLabel,
        rooftop_name: detailsToUse?.rooftop_name || fallbackCoordsLabel,
        name: detailsToUse?.name || fallbackCoordsLabel,
        display_text: detailsToUse?.display_text || `${fallbackCoordsLabel}`,
        display_address: detailsToUse?.display_address || `${fallbackCoordsLabel}`
      };
      return { details: nextDetails, label: fallbackCoordsLabel };
    };

    if (point === 'travelMode') {
      // Travel mode changed - update and refetch route if both points exist
      routing.setTravelMode(coords); // coords contains the travel mode string
      setSelectedRouteIndex(0); // Reset selected route when travel mode changes
      if (routing.routePointA && routing.routePointB) {
        routing.hasZoomedToRouteRef.current = false;
        await routing.fetchRoute(
          routing.routePointA,
          routing.routePointB,
          routePointADetails || routing.routeDetails?.pointADetails,
          routePointBDetails || routing.routeDetails?.pointBDetails,
          routeStopsRef.current,
          coords // Pass the new travel mode directly
        );
      }
      return;
    }

    if (point === 'swap') {
      // Swap operation - swap the points and fetch new route
      let { newA, newB, detailsA, detailsB } = coords;

      const isUserLocationPoint = (p) =>
        userLocation &&
        p &&
        typeof p.lat === 'number' &&
        typeof p.lng === 'number' &&
        Math.abs(p.lat - userLocation.lat) < 1e-5 &&
        Math.abs(p.lng - userLocation.lng) < 1e-5;

      // If new source (old destination) is at user's location, show source as "Your Location"
      if (newA && isUserLocationPoint(newA)) {
        detailsA = getUserLocationDetails(userLocation);
      }

      // If new destination (old source) was "Your Location", resolve the actual rooftop name for the MAP LABEL only
      let mapLabelDetailsB = detailsB;
      if (newB && isUserLocationPoint(newB) && 
          (detailsB?.entity_name === 'Your Location' || detailsB?.name === 'Your Location')) {
        try {
          const resolved = await fetchLocationDetails(newB.lat, newB.lng);
          if (resolved) {
            mapLabelDetailsB = resolved;
          }
        } catch (e) {
          // Keep existing details on failure
        }
        // detailsB stays as "Your Location" for the panel
      }

      const resolvedA = newA
        ? await ensureReadableRouteLabel(newA, detailsA, 'Start')
        : { details: detailsA, label: resolveLabel(detailsA, 'Start') };
      const resolvedB = newB
        ? await ensureReadableRouteLabel(newB, detailsB, 'Destination')
        : { details: detailsB, label: resolveLabel(detailsB, 'Destination') };
      // Resolve map label for B separately (uses actual rooftop name instead of "Your Location")
      const resolvedBMapLabel = newB
        ? await ensureReadableRouteLabel(newB, mapLabelDetailsB, 'Destination')
        : { details: mapLabelDetailsB, label: resolveLabel(mapLabelDetailsB, 'Destination') };

      // Update the route points
      routing.setRoutePointA(newA);
      routing.setRoutePointB(newB);
      setRoutePointADetails(resolvedA.details || null);
      setRoutePointBDetails(resolvedB.details || null);

      // Remove existing markers
      routing.routeMarkersRef.current.forEach(m => m.remove());
      routing.routeMarkersRef.current = [];

      // Add new markers with swapped positions
      if (newA) {
        routing.addRouteMarker(
          newA.lat,
          newA.lng,
          'A',
          resolvedA.label
        );
      }
      if (newB) {
        routing.addRouteMarker(
          newB.lat,
          newB.lng,
          'B',
          resolvedBMapLabel.label
        );
      }

      // Fetch the new route with swapped points
      // Pass pre-fetched details to avoid redundant RVG API calls
      // Also pass current travel mode to ensure correct vehicle type is used
      if (newA && newB) {
        routing.hasZoomedToRouteRef.current = false;
        setSelectedRouteIndex(0); // Reset selected route when swapping
        await routing.fetchRoute(newA, newB, resolvedA.details, resolvedB.details, routeStopsRef.current, routing.travelMode);

        // After fetchRoute, restore the resolved map label for B if it was at user's location
        // (fetchRoute's updateRouteMarkerWithName would have overwritten it with "Your Location")
        if (newB && isUserLocationPoint(newB) && resolvedBMapLabel.label !== 'Your Location') {
          routing.updateRouteMarkerWithName('B', resolvedBMapLabel.label, newB.lat, newB.lng);
        }
      }
      return;
    }

    if (point === 'reorder') {
      // Atomic reorder operation from drag-and-drop
      // Updates A, B, stops, markers, and fetches route in one go
      const { source, destination, stops } = coords;
      
      // Update stops ref
      routeStopsRef.current = stops || [];
      
      // Clear existing route visualization
      routing.clearRouteVisualization();
      routing.setRouteDetails(null);
      routing.hasZoomedToRouteRef.current = false;
      setSelectedRouteIndex(0);

      // Resolve "Your Location" labels to actual rooftop names for non-source positions
      const resolveYourLocationDetails = async (details, location) => {
        if (!details || !location) return details;
        if (details.entity_name !== 'Your Location' && details.name !== 'Your Location') return details;
        try {
          const resolved = await fetchLocationDetails(location.lat, location.lng);
          return resolved || details;
        } catch (e) {
          return details;
        }
      };

      const resolveYourLocationStop = async (stop) => {
        if (!stop || stop.name !== 'Your Location') return stop;
        try {
          const resolved = await fetchLocationDetails(stop.lat, stop.lng);
          if (resolved) {
            // Return stop with resolved name for map marker, but keep original name for panel
            return { ...stop, resolvedMapName: resolved.entity_name || resolved.rooftop_name || resolved.name || stop.name };
          }
        } catch (e) {
          // Keep original on failure
        }
        return stop;
      };

      // Source keeps "Your Location" if it's the user's location, others get resolved for map labels only
      const resolvedSourceDetails = source?.details;
      const resolvedDestDetails = await resolveYourLocationDetails(destination?.details, destination?.location);
      const resolvedStops = stops ? await Promise.all(stops.map(resolveYourLocationStop)) : [];

      // Keep "Your Location" for the panel, use resolved name only for map markers
      const isDestYourLocation = destination?.details?.entity_name === 'Your Location' || destination?.details?.name === 'Your Location';
      const panelDestDetails = isDestYourLocation ? destination?.details : resolvedDestDetails;

      // For stops, keep original names for panel but use resolvedMapName for map markers
      // The routeStopsRef should keep original names for the panel
      routeStopsRef.current = resolvedStops.map(stop => {
        // Strip resolvedMapName from the ref (panel uses 'name')
        const { resolvedMapName, ...panelStop } = stop;
        return panelStop;
      });
      
      // Update source (point A)
      if (source) {
        routing.setRoutePointA(source.location);
        setRoutePointADetails(resolvedSourceDetails || null);
        
        // Update A marker
        const oldAIndex = routing.routeMarkersRef.current.findIndex(m => m._label === 'A');
        if (oldAIndex !== -1) {
          routing.routeMarkersRef.current[oldAIndex].remove();
          routing.routeMarkersRef.current.splice(oldAIndex, 1);
        }
        const aLabel = resolveLabel(resolvedSourceDetails, 'Start');
        routing.addRouteMarker(source.location.lat, source.location.lng, 'A', aLabel);
      }
      
      // Update destination (point B)
      if (destination) {
        routing.setRoutePointB(destination.location);
        setRoutePointBDetails(panelDestDetails || null);
        
        // Update B marker - use resolved name for map label
        const oldBIndex = routing.routeMarkersRef.current.findIndex(m => m._label === 'B');
        if (oldBIndex !== -1) {
          routing.routeMarkersRef.current[oldBIndex].remove();
          routing.routeMarkersRef.current.splice(oldBIndex, 1);
        }
        const bLabel = resolveLabel(resolvedDestDetails, 'Destination');
        routing.addRouteMarker(destination.location.lat, destination.location.lng, 'B', bLabel);
      }
      
      // Remove old stop markers
      routing.routeMarkersRef.current
        .filter(m => m._label && m._label.startsWith('stop-'))
        .forEach(m => m.remove());
      routing.routeMarkersRef.current = routing.routeMarkersRef.current
        .filter(m => !m._label || !m._label.startsWith('stop-'));

      // Update panel waypoint names for any resolved "Your Location" stops/destination
      if (routingPanelRef.current) {
        resolvedStops.forEach((stop, idx) => {
          if (stops[idx] && stops[idx].name === 'Your Location' && stop.name !== 'Your Location') {
            // Stop waypoints are at indices 1..N-1 in the panel (0 is source, last is destination)
            routingPanelRef.current.setWaypointLocation(idx + 1, { lat: stop.lat, lng: stop.lng }, stop.name);
          }
        });
        // Don't update the panel destination name if it was "Your Location" — keep it as-is in the sidebar
      }
      
      // Fetch route once with all updated data
      if (source?.location && destination?.location) {
        await routing.fetchRoute(
          source.location,
          destination.location,
          resolvedSourceDetails,
          panelDestDetails,
          resolvedStops,
          routing.travelMode
        );

        // After fetchRoute, restore the resolved map label for B if it was "Your Location"
        if (isDestYourLocation && resolvedDestDetails) {
          const bMapLabel = resolvedDestDetails.entity_name || resolvedDestDetails.rooftop_name || resolvedDestDetails.name;
          if (bMapLabel && bMapLabel !== 'Your Location') {
            routing.updateRouteMarkerWithName('B', bMapLabel, destination.location.lat, destination.location.lng);
          }
        }
      }
      return;
    }

    if (point === 'clearA') {
      const existingB = routing.routePointB;
      const existingDetails = routePointBDetails || routing.routeDetails?.pointBDetails;
      setRoutePointADetails(null);
      routing.setRoutePointA(null);
      if (existingB) {
        setRoutePointBDetails(existingDetails || null);
      }
      return;
    }

    if (point === 'clearB') {
      const existingA = routing.routePointA;
      const existingDetails = routePointADetails || routing.routeDetails?.pointADetails;
      setRoutePointBDetails(null);
      routing.setRoutePointB(null);
      if (existingA) {
        setRoutePointADetails(existingDetails || null);
      }
      return;
    }

    if (point === 'A') {
      routing.clearRouteVisualization();
      routing.setRouteDetails(null);
      routing.hasZoomedToRouteRef.current = false;
      setSelectedRouteIndex(0); // Reset selected route when source changes
      const resolved = await ensureReadableRouteLabel(coords, details, 'Start');
      routing.setRoutePointA(coords);
      setRoutePointADetails(resolved.details || null);
      const oldMarkerIndex = routing.routeMarkersRef.current.findIndex(m => m._label === 'A');
      if (oldMarkerIndex !== -1) {
        routing.routeMarkersRef.current[oldMarkerIndex].remove();
        routing.routeMarkersRef.current.splice(oldMarkerIndex, 1);
      }
      routing.addRouteMarker(coords.lat, coords.lng, 'A', resolved.label);
      if (routing.routePointB) {
        clearRvgUi();
        routing.hasZoomedToRouteRef.current = false;
        // Pass details for point A if available, and use existing routeDetails for point B
        // Also pass current travel mode to ensure correct vehicle type is used
        await routing.fetchRoute(
          coords,
          routing.routePointB,
          resolved.details,
          routePointBDetails || routing.routeDetails?.pointBDetails,
          routeStopsRef.current,
          routing.travelMode
        );
      }
      return;
    }

    if (point === 'B') {
      routing.clearRouteVisualization();
      routing.setRouteDetails(null);
      routing.hasZoomedToRouteRef.current = false;
      setSelectedRouteIndex(0); // Reset selected route when destination changes
      const resolved = await ensureReadableRouteLabel(coords, details, 'Destination');
      routing.setRoutePointB(coords);
      setRoutePointBDetails(resolved.details || null);
      const oldMarkerIndex = routing.routeMarkersRef.current.findIndex(m => m._label === 'B');
      if (oldMarkerIndex !== -1) {
        routing.routeMarkersRef.current[oldMarkerIndex].remove();
        routing.routeMarkersRef.current.splice(oldMarkerIndex, 1);
      }
      routing.addRouteMarker(coords.lat, coords.lng, 'B', resolved.label);
      if (routing.routePointA) {
        clearRvgUi();
        routing.hasZoomedToRouteRef.current = false;
        // Pass existing routeDetails for point A, and details for point B if available
        // Also pass current travel mode to ensure correct vehicle type is used
        await routing.fetchRoute(
          routing.routePointA,
          coords,
          routePointADetails || routing.routeDetails?.pointADetails,
          resolved.details,
          routeStopsRef.current,
          routing.travelMode
        );
      }
    }
  }, [routing, routePointADetails, routePointBDetails, userLocation, getUserLocationDetails, clearRvgUi, fetchLocationDetails]);

  const handleRouteMarkerDrag = useCallback(async (label, coords) => {
    if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
      return;
    }
    const locationDetails = await fetchLocationDetails(coords.lat, coords.lng);
    const fallbackLabel = label === 'A' ? 'Start Location' : 'Destination';
    const resolvedLabel =
      locationDetails?.entity_name ||
      locationDetails?.display_name ||
      locationDetails?.name ||
      fallbackLabel;
    const normalizedDetails = locationDetails || {
      entity_name: resolvedLabel,
      display_name: resolvedLabel,
      name: resolvedLabel,
      display_text: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
      display_address: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
      location: { lat: coords.lat, lon: coords.lng, long: coords.lng },
      clickedLocation: { lat: coords.lat, long: coords.lng }
    };
    await handleRoutePointsChange(label, coords, normalizedDetails);
  }, [fetchLocationDetails, handleRoutePointsChange]);

  useEffect(() => {
    routePointDragHandlerRef.current = handleRouteMarkerDrag;
  }, [handleRouteMarkerDrag, routePointDragHandlerRef]);

  // -----------------------------------
  // 🖱️ MAP CLICK HANDLER
  // -----------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const runClickAction = async (e) => {
      const lat = e.lngLat.lat;
      const lng = e.lngLat.lng;
      // eslint-disable-next-line no-unused-vars
      const clickedCoords = { lat, lng };

      // Check if click is on a street view point - if so, skip RVG
      if (streetView.isStreetViewEnabled && map.getLayer('street-view-circles')) {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['street-view-circles']
        });

        if (features && features.length > 0) {
          // Click is on a street view point, let the street view handler deal with it
          return;
        }
      }

      if (routing.isRoutingMode) {
        // In routing mode, clicks should set the active route point only if empty
        const isDrawingActive = drawRef.current && drawRef.current.getMode &&
          ['draw_polygon', 'draw_line_string', 'draw_point'].includes(drawRef.current.getMode());
        if (isDrawingActive) {
          return;
        }

        // Check if click is on a route layer (main or alternate) - if so, skip RVG
        const routeLayers = map.getStyle().layers
          .filter(layer => layer.id.startsWith('route-') || layer.id.startsWith('alternate-route-'))
          .map(layer => layer.id);
        
        if (routeLayers.length > 0) {
          const routeFeatures = map.queryRenderedFeatures(e.point, { layers: routeLayers });
          if (routeFeatures && routeFeatures.length > 0) {
            // Click is on a route line, let the route click handler deal with it
            return;
          }
        }

        const hasPointA = Boolean(routing.routePointA);
        const hasPointB = Boolean(routing.routePointB);

        // If any waypoint input (source, stop, or dest) is focused, set the clicked location there
        if ((routingActiveInput === 'source' || routingActiveInput === 'stop' || routingActiveInput === 'dest') && routingActiveWaypointIndex != null && routingPanelRef.current) {
          const locationDetails = await fetchLocationDetails(lat, lng);
          const resolvedLabel =
            locationDetails?.entity_name ||
            locationDetails?.display_name ||
            locationDetails?.name ||
            'Dropped Pin';
          routingPanelRef.current.setWaypointLocation(routingActiveWaypointIndex, { lat, lng }, resolvedLabel);
          return;
        }

        if (hasPointA && hasPointB) {
          // Both inputs filled: do not change route pins, show RVG instead
          await reverseGeocode.callReverseGeocode(lat, lng, clickedCoords, true, isDrawingActive);
          return;
        }

        // No input is focused — treat as regular RVG click
        if (!routingActiveInput) {
          await reverseGeocode.callReverseGeocode(lat, lng, clickedCoords, true, isDrawingActive);
          return;
        }

        const locationDetails = await fetchLocationDetails(lat, lng);
        const fallbackLabel = 'Dropped Pin';
        const resolvedLabel =
          locationDetails?.entity_name ||
          locationDetails?.display_name ||
          locationDetails?.name ||
          fallbackLabel;

        const normalizedDetails = locationDetails || {
          entity_name: resolvedLabel,
          display_name: resolvedLabel,
          name: resolvedLabel,
          display_text: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          display_address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          location: { lat, lon: lng, long: lng },
          clickedLocation: { lat, long: lng }
        };

        let targetPoint = 'A';
        if (routingActiveInput === 'source' && !hasPointA) {
          targetPoint = 'A';
        } else if (routingActiveInput === 'dest' && !hasPointB) {
          targetPoint = 'B';
        } else if (!hasPointA) {
          targetPoint = 'A';
        } else if (!hasPointB) {
          targetPoint = 'B';
        }

        await handleRoutePointsChange(targetPoint, { lat, lng }, normalizedDetails);
        return;
      }

      if (reverseGeocode.showPopup && reverseGeocode.popupData) {
        clearRvgUi();
        return;
      }

      // Truck mode takes priority
      if (showTruckPanel && truck.waypoints.length < 5) {
        // Determine waypoint type based on count
        let type = 'facility';
        let name = `Facility ${truck.waypoints.length}`;

        if (truck.waypoints.length === 0) {
          type = 'source';
          name = 'Source';
        } else if (truck.waypoints.length === 4) {
          type = 'destination';
          name = 'Destination';
        }

        // Fetch location name
        const locationDetails = await fetchLocationDetails(lat, lng);
        if (locationDetails) {
          name = locationDetails.rooftop_name;
        }

        truck.setWaypoints([...truck.waypoints, { lat, lng, name, type }]);
        return;
      }

      if (isRvgMode) {
        // RVG Mode (or Routing Mode): Call reverse geocoding API with marker only (no sidebar)
        // Check if we're in drawing mode - if so, skip centering
        const isDrawingActive = drawRef.current && drawRef.current.getMode &&
          ['draw_polygon', 'draw_line_string', 'draw_point'].includes(drawRef.current.getMode());
        clearSearchBar();
        await reverseGeocode.callReverseGeocode(lat, lng, clickedCoords, true, isDrawingActive);
      } else {
        // Normal mode: do nothing (no hover card)
        // User can use RVG mode or search to interact with features
      }
    };

    const handleClick = (e) => {
      // Clear any previous pending single-click so only the latest click is considered
      if (pendingMapClickTimeoutRef.current) {
        clearTimeout(pendingMapClickTimeoutRef.current);
        pendingMapClickTimeoutRef.current = null;
      }
      // Short delay so double-click can cancel single-click action (zoom only); single-click still feels quick
      const delayMs = 250;
      pendingMapClickTimeoutRef.current = setTimeout(() => {
        pendingMapClickTimeoutRef.current = null;
        runClickAction(e);
      }, delayMs);
    };

    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
      // Do NOT clear pending timeout here: effect re-runs after zoom/move (new deps) would cancel
      // the next click's RVG. Timeout is cleared only on unmount (see effect below).
    };
  }, [
    routing,
    fetchLocationDetails,
    showTruckPanel,
    truck,
    isRvgMode,
    reverseGeocode,
    streetView.isStreetViewEnabled,
    clearRvgUi,
    handleRoutePointsChange,
    routingActiveInput,
    routingActiveWaypointIndex,
    clearSearchBar
  ]);

  // Clear pending map-click timeout only on unmount (so re-renders after zoom don't kill the next click)
  useEffect(() => {
    return () => {
      if (pendingMapClickTimeoutRef.current) {
        clearTimeout(pendingMapClickTimeoutRef.current);
        pendingMapClickTimeoutRef.current = null;
      }
    };
  }, []);

  // -----------------------------------
  // 🔍 SEARCH RESULT HANDLER
  // -----------------------------------
  const handleResultSelect = async (result) => {
    // Exit routing mode if enabled
    if (routing.isRoutingMode) {
      routing.clearRoute();
      routing.setIsRoutingMode(false);
    }

    if (reverseGeocode.showPopup && reverseGeocode.popupData) {
      clearRvgUi();
    }

    // Get coordinates - search API may use 'long' or 'lon' depending on normalization
    const lat = result.lat;
    const lng = result.long || result.lon;

    if (!lat || !lng) {
      return;
    }

    // Remove existing search result marker if present
    if (searchResultMarkerRef.current) {
      searchResultMarkerRef.current.remove();
      searchResultMarkerRef.current = null;
    }

    // Clear existing RVG markers and shapes
    reverseGeocode.clearRvgMarkers();
    if (mapRef.current.getLayer('rvg-shape-fill')) {
      mapRef.current.removeLayer('rvg-shape-fill');
    }
    if (mapRef.current.getLayer('rvg-shape-outside')) {
      mapRef.current.removeLayer('rvg-shape-outside');
    }
    if (mapRef.current.getLayer('rvg-shape-outline')) {
      mapRef.current.removeLayer('rvg-shape-outline');
    }
    if (mapRef.current.getSource('rvg-shape')) {
      mapRef.current.removeSource('rvg-shape');
    }
    if (mapRef.current.getSource('rvg-shape-mask')) {
      mapRef.current.removeSource('rvg-shape-mask');
    }

    // Always add a marker at the location (RVG pin)
    const el = document.createElement('div');
    el.className = 'rvg-pin-marker';
    el.style.cursor = "pointer";

    // Create marker
    const marker = new maplibregl.Marker({
      element: el,
      anchor: 'bottom'
    })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    // Ensure RVG pin renders above POI icons
    const markerEl1 = marker.getElement();
    if (markerEl1) markerEl1.style.zIndex = '2000';

    searchResultMarkerRef.current = marker;

    // Use search result data directly - NO reverse geocoding API call needed!
    // Parse WKT polygon/multipolygon if shape exists
    let shapeGeometry = null;
    if (result.shape && typeof result.shape === 'string') {
      shapeGeometry = parseWKTGeometry(result.shape);
    } else if (result.shape && result.shape.coordinates) {
      const isMultiPolygon = Array.isArray(result.shape.coordinates?.[0]?.[0]?.[0]);
      shapeGeometry = {
        type: result.shape.type || (isMultiPolygon ? 'MultiPolygon' : 'Polygon'),
        coordinates: result.shape.coordinates
      };
    }

    // Render shape on map if available
    if (shapeGeometry) {
      const buildInverseMaskFeature = (geometry) => {
        if (!geometry || !geometry.coordinates) return null;
        const worldBoundsRing = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];
        let holeRings = [];

        if (geometry.type === 'Polygon') {
          holeRings = geometry.coordinates;
        } else if (geometry.type === 'MultiPolygon') {
          holeRings = geometry.coordinates.flat();
        }

        if (!holeRings.length) return null;

        return {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [worldBoundsRing, ...holeRings]
          }
        };
      };

      mapRef.current.addSource('rvg-shape', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: shapeGeometry.type,
            coordinates: shapeGeometry.coordinates
          }
        }
      });

      const inverseMaskFeature = buildInverseMaskFeature(shapeGeometry);
      if (inverseMaskFeature) {
        mapRef.current.addSource('rvg-shape-mask', {
          type: 'geojson',
          data: inverseMaskFeature
        });

        // Darken only the outside area.
        mapRef.current.addLayer({
          id: 'rvg-shape-outside',
          type: 'fill',
          source: 'rvg-shape-mask',
          paint: {
            'fill-color': '#000000',
            'fill-opacity': 0.28
          }
        });
      }

      // Keep the selected polygon interior visually lighter.
      mapRef.current.addLayer({
        id: 'rvg-shape-fill',
        type: 'fill',
        source: 'rvg-shape',
        paint: {
          'fill-color': '#ffffff',
          'fill-opacity': 0.08
        }
      });

      // Add outline layer
      mapRef.current.addLayer({
        id: 'rvg-shape-outline',
        type: 'line',
        source: 'rvg-shape',
        paint: {
          'line-color': '#dc3545',
          'line-width': 2
        }
      });

      // Fit map to show entire shape
      const coordinates = getGeometryCoordinates(shapeGeometry);
      if (coordinates.length > 0) {
        const bounds = coordinates.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));

        mapRef.current.fitBounds(bounds, {
          padding: 50,
          duration: 1000,
          maxZoom: 17
        });
      }
    } else {
      // No shape - just center on the location
      mapRef.current.flyTo({
        center: [lng, lat],
        duration: 1200,
        zoom: 16.5
      });
    }

    // Prepare popup data from search result (no API call needed!)
    const normalizedData = {
      entity_id: result.entity_id ?? result.id ?? null,
      entity_name: result.entity_name || result.display_text || result.display_name || result.name,
      entity_type: result.entity_type || result.type || null,
      name: result.entity_name || result.display_text || result.display_name || result.name,
      display_name: result.entity_name || result.display_text || result.display_name || result.name,
      rooftop_name: result.entity_name || result.display_text || result.display_name || result.name,
      display_text: result.display_text || result.display_address || '',
      display_address: result.display_text || result.display_address || '',
      full_address: result.full_address || result.display_address || '',
      type: result.entity_type || result.type || 'location',
      score: result.score ?? null,
      distance_m: 0,
      source: 'SEARCH',
      location: {
        lat: lat,
        lon: lng,
        long: lng
      },
      clickedLocation: {
        lat: lat,
        long: lng
      }
    };

    // Show bottom card for search results, including polygons
    reverseGeocode.setPopupPosition({ lng: lng, lat: lat });
    reverseGeocode.setPopupData(normalizedData);
    reverseGeocode.setShowPopup(true);

    reverseGeocode.setRvgResults([normalizedData]);
    reverseGeocode.setShowSidebar(false);
  };

  useEffect(() => {
    if (reverseGeocode.popupData?.location) {
      const { location } = reverseGeocode.popupData;
      const lat = location.lat;
      const lng = location.lon || location.long;
      if (lat && lng) {
        lastPinnedLocationRef.current = {
          lat,
          lng,
          name:
            reverseGeocode.popupData.entity_name ||
            reverseGeocode.popupData.display_name ||
            reverseGeocode.popupData.name ||
            reverseGeocode.popupData.display_text ||
            '',
          data: reverseGeocode.popupData
        };
        return;
      }
    }

    const fallbackData = reverseGeocode.rvgResults?.[0];
    if (fallbackData) {
      const location = fallbackData.location || fallbackData.centroid_point;
      const lat = location?.lat ?? fallbackData.lat;
      const lng =
        location?.long ??
        location?.lon ??
        location?.lng ??
        fallbackData.long ??
        fallbackData.lon ??
        fallbackData.lng;
      if (lat && lng) {
        lastPinnedLocationRef.current = {
          lat,
          lng,
          name:
            fallbackData.entity_name ||
            fallbackData.display_name ||
            fallbackData.name ||
            fallbackData.rooftop_name ||
            fallbackData.display_text ||
            '',
          data: fallbackData
        };
      }
    }
  }, [reverseGeocode.popupData, reverseGeocode.rvgResults]);

  const getShortAddress = useCallback((value = '') => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    const parts = value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    return parts[0] || value.trim();
  }, []);

  const resolvePinnedLabel = useCallback((data = {}, fallback = '') => {
    const primary =
      data?.entity_name ||
      data?.display_name ||
      data?.name ||
      data?.rooftop_name;
    if (primary && String(primary).trim()) {
      return String(primary).trim();
    }
    const addressText =
      data?.display_text ||
      data?.display_address ||
      data?.entity_address ||
      data?.full_address;
    return getShortAddress(addressText) || fallback;
  }, [getShortAddress]);

  const handlePoiSuggestionSelect = useCallback((suggestion) => {
    const categoryId = suggestion?.poiCategoryId;
    if (!categoryId) {
      return;
    }

    const categoryObj = Object.values(POI_CATEGORIES).find((category) => category.id === categoryId);
    if (!categoryObj) {
      return;
    }

    // Select only the POIs for the chosen category (no API search call)
    poi.setSelectedAmenities(categoryObj.amenities);
    poi.setSidebarCategory(categoryObj.id);
    poi.setShowPoiSidebar(true);
  }, [poi]);

  const handleCloseRvgCard = useCallback(() => {
    clearRvgUi();
  }, [clearRvgUi]);

  const handleNavigateFromRvgCard = useCallback(() => {
    const currentData = reverseGeocode.popupData;
    if (!currentData || !currentData.location) {
      return;
    }

    const destination = {
      lat: currentData.location.lat,
      lng: currentData.location.lon || currentData.location.long
    };

    const existingSource = routing.routePointA;
    const existingSourceDetails = routePointADetails || routing.routeDetails?.pointADetails;
    const hasExistingSource =
      existingSource &&
      typeof existingSource.lat === 'number' &&
      typeof existingSource.lng === 'number' &&
      !isNaN(existingSource.lat) &&
      !isNaN(existingSource.lng);
    const shouldUseExistingSource = hasExistingSource;
    const sourcePoint = shouldUseExistingSource
      ? existingSource
      : (userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : null);
    const sourceDetails = shouldUseExistingSource
      ? existingSourceDetails
      : (userLocation ? getUserLocationDetails(userLocation) : null);
    const sourceLabel = shouldUseExistingSource
      ? resolvePinnedLabel(existingSourceDetails, 'Start Location')
      : 'Your Location';

    handleCloseRvgCard();

    // Close RVG sidebar and disable RVG mode
    reverseGeocode.setShowSidebar(false);
    reverseGeocode.setRvgResults([]);
    setIsRvgMode(false);

    // Close POI sidebar if open
    poi.setShowPoiSidebar(false);
    poi.setSidebarPois([]);
    poi.setSidebarCategory(null);
    poi.setSelectedAmenities([]);

    // Ensure routing mode is open without toggling it off
    if (!routing.isRoutingMode) {
      routing.setIsRoutingMode(true);
    }

    // Clear any existing route/markers before setting new destination
    routing.clearRoute();
    if (routingPanelRef.current?.resetWaypoints) {
      routingPanelRef.current.resetWaypoints();
    }

    routing.setRoutePointB(destination);
    setRoutePointBDetails(currentData);
    routing.addRouteMarker(
      destination.lat,
      destination.lng,
      'B',
      currentData.entity_name || currentData.display_name || currentData.name
    );

    if (sourcePoint) {
      setPrefilledRoutingDestinationName('');
      routing.setRoutePointA(sourcePoint);
      setRoutePointADetails(sourceDetails || null);
      routing.addRouteMarker(sourcePoint.lat, sourcePoint.lng, 'A', sourceLabel || 'Start Location');

      // Automatically fetch and display the route
      // Pass currentData as pre-fetched details for point B to avoid redundant API call
      routing.fetchRoute(sourcePoint, destination, sourceDetails, currentData);
    } else {
      // No current location: keep source empty and prefill destination
      routing.setRoutePointA(null);
      setRoutePointBDetails(currentData);
      setPrefilledRoutingDestinationName(
        currentData.entity_name || currentData.display_name || currentData.name || ''
      );
    }
  }, [getUserLocationDetails, handleCloseRvgCard, poi, resolvePinnedLabel, reverseGeocode, routePointADetails, routing, setIsRvgMode, setPrefilledRoutingDestinationName, userLocation]);

  const handleDirectionsFromSearch = useCallback((result) => {
    if (!result) {
      return;
    }

    const lat = result.lat;
    const lng = result.long || result.lon;
    if (!lat || !lng) {
      return;
    }

    // Close RVG sidebar and disable RVG mode
    reverseGeocode.setShowSidebar(false);
    reverseGeocode.setRvgResults([]);
    reverseGeocode.setHighlightedResultIndex(null);
    reverseGeocode.clearRvgMarkers();
    reverseGeocode.clearRvgPolygon();
    setIsRvgMode(false);

    // Close POI sidebar if open
    poi.setShowPoiSidebar(false);
    poi.setSidebarPois([]);
    poi.setSidebarCategory(null);
    poi.setSelectedAmenities([]);

    // Remove search result marker
    if (searchResultMarkerRef.current) {
      searchResultMarkerRef.current.remove();
      searchResultMarkerRef.current = null;
    }

    if (!routing.isRoutingMode) {
      routing.toggleRoutingMode();
    }

    routing.clearRoute();
    if (routingPanelRef.current?.resetWaypoints) {
      routingPanelRef.current.resetWaypoints();
    }

    const locationPoint = { lat, lng };
    const locationName = result.entity_name || result.display_text || result.display_name || result.name;

    if (userLocation) {
      const userLocationDetails = getUserLocationDetails(userLocation);
      setPrefilledRoutingDestinationName('');
      routing.setRoutePointB(locationPoint);
      routing.addRouteMarker(locationPoint.lat, locationPoint.lng, 'B', locationName);
      routing.setRoutePointA(userLocation);
      setRoutePointADetails(userLocationDetails);
      routing.addRouteMarker(userLocation.lat, userLocation.lng, 'A', 'Your Location');

      // Pass search result as pre-fetched details for point B
      routing.fetchRoute(userLocation, locationPoint, userLocationDetails, result);
    } else {
      // No current location - at least fill destination with the selected result
      routing.setRoutePointB(locationPoint);
      routing.setRoutePointA(null);
      routing.addRouteMarker(locationPoint.lat, locationPoint.lng, 'B', locationName);
      setPrefilledRoutingDestinationName(locationName || '');
    }
  }, [getUserLocationDetails, poi, reverseGeocode, routing, setIsRvgMode, setPrefilledRoutingDestinationName, userLocation]);

  useEffect(() => {
    if (internalUrlCardInitializedRef.current) {
      return;
    }
    const pathname = window.location?.pathname || '';
    if (pathname !== '/internal') {
      internalUrlCardInitializedRef.current = true;
      return;
    }

    let retryTimer = null;

    const initInternalCardFromUrl = () => {
      if (internalUrlCardInitializedRef.current) {
        return;
      }

      const map = mapRef.current;
      if (!map) {
        retryTimer = setTimeout(initInternalCardFromUrl, 200);
        return;
      }

      const params = new URLSearchParams(window.location.search || '');
      const latlngParam = params.get('latlng') || '';
      const addressParam = params.get('address') || '';
      const localityParam = params.get('locality') || '';

      const [rawLat, rawLng] = latlngParam.split(',');
      const lat = Number.parseFloat(rawLat);
      const lng = Number.parseFloat(rawLng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        internalUrlCardInitializedRef.current = true;
        return;
      }

      const normalizeQueryText = (value = '') => String(value).replace(/\s+/g, ' ').trim();
      const normalizedAddress = normalizeQueryText(addressParam);
      const normalizedLocality = normalizeQueryText(localityParam);

      if (searchResultMarkerRef.current) {
        searchResultMarkerRef.current.remove();
        searchResultMarkerRef.current = null;
      }
      if (searchMarkerRef.current) {
        searchMarkerRef.current.remove();
        searchMarkerRef.current = null;
      }

      const markerElement = document.createElement('div');
      markerElement.className = 'rvg-pin-marker';
      markerElement.style.cursor = 'pointer';

      searchResultMarkerRef.current = new maplibregl.Marker({
        element: markerElement,
        anchor: 'bottom'
      })
        .setLngLat([lng, lat])
        .addTo(map);

      // Ensure RVG pin renders above POI icons
      const rvgEl = searchResultMarkerRef.current.getElement();
      if (rvgEl) rvgEl.style.zIndex = '2000';

      map.flyTo({
        center: [lng, lat],
        duration: 800,
        zoom: Math.max(map.getZoom(), 15)
      });

      const fallbackName = normalizedAddress || normalizedLocality || 'Selected Location';
      const internalPopupData = {
        entity_name: fallbackName,
        display_name: fallbackName,
        name: fallbackName,
        rooftop_name: fallbackName,
        display_text: normalizedAddress,
        display_address: normalizedAddress,
        full_address: normalizedAddress,
        locality: normalizedLocality,
        source: 'INTERNAL_URL',
        location: {
          lat,
          lon: lng,
          long: lng
        },
        clickedLocation: {
          lat,
          long: lng
        }
      };

      reverseGeocode.setShowSidebar(false);
      reverseGeocode.setRvgResults([internalPopupData]);
      reverseGeocode.setPopupPosition({ lng, lat });
      reverseGeocode.setPopupData(internalPopupData);
      reverseGeocode.setShowPopup(true);
      internalUrlCardInitializedRef.current = true;
    };

    initInternalCardFromUrl();

    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [reverseGeocode]);

  // -----------------------------------
  // 🎨 CLOSE POI DROPDOWN ON OUTSIDE CLICK
  // -----------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (poi.openPoiCategory) {
        if (!event.target.closest('.poi-categories-container')) {
          poi.setOpenPoiCategory(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [poi]);

  // -----------------------------------
  // 🚚 TRUCK ANIMATION HANDLERS
  // -----------------------------------

  // Update waypoint markers when waypoints change
  useEffect(() => {
    if (showTruckPanel && truck.waypoints.length > 0) {
      truck.addWaypointMarkers(truck.waypoints);
    }
  }, [truck.waypoints, showTruckPanel, truck]);

  // (Hover card is now controlled only by click handler in the map click effect)

  const addTruckRouteArrows = useCallback((map) => {
    const arrowImageId = 'route-arrow-icon';
    const arrowUrl = `${process.env.PUBLIC_URL || ''}/icons/routeArrow.png`;
    const layerId = 'truck-route-arrows';

    const addLayer = () => {
      if (map.getLayer && map.getLayer(layerId)) {
        return;
      }
      map.addLayer({
        id: layerId,
        type: 'symbol',
        source: 'truck-route',
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 40,
          'icon-image': arrowImageId,
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotation-alignment': 'map',
          'icon-keep-upright': false,
          'icon-pitch-alignment': 'map'
        }
      });
    };

    if (map.hasImage && map.hasImage(arrowImageId)) {
      addLayer();
      return;
    }

    if (map.loadImage) {
      map.loadImage(arrowUrl, (error, image) => {
        if (error || !image) {
          return;
        }
        if (map.hasImage && !map.hasImage(arrowImageId)) {
          map.addImage(arrowImageId, image);
        }
        addLayer();
      });
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (map.hasImage && !map.hasImage(arrowImageId)) {
        map.addImage(arrowImageId, img);
      }
      addLayer();
    };
    img.onerror = (error) => {
    };
    img.src = arrowUrl;
  }, []);

  const handleStartTruckAnimation = async () => {
    if (truck.waypoints.length < 2) {
      alert('Add at least 2 waypoints to create a route');
      return;
    }

    try {
      // Fetch route for all waypoints
      const route = await getMultiWaypointRoute(truck.waypoints);

      // Draw route on map
      if (mapRef.current) {
        const map = mapRef.current;

        // Check if map is loaded and valid
        if (!map || !map.getStyle || !map.getStyle()) {
          return;
        }

        try {
          if (map.getSource && map.getSource('truck-route')) {
            map.getSource('truck-route').setData({
              type: 'Feature',
              properties: {},
              geometry: route.geometry
            });
          } else {
            map.addSource('truck-route', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: route.geometry
              }
            });

            map.addLayer({
              id: 'truck-route-line',
              type: 'line',
              source: 'truck-route',
              paint: {
                'line-color': '#ff6b35',
                'line-width': 5,
                'line-opacity': 0.8
              }
            });
          }

          addTruckRouteArrows(map);

          // Fit map to show entire route
          const coordinates = route.geometry.coordinates;
          const bounds = new maplibregl.LngLatBounds();
          coordinates.forEach(coord => bounds.extend(coord));
          map.fitBounds(bounds, { padding: 100, duration: 1000 });
        } catch (error) {
          // Error drawing route
        }
      }

      truck.setRouteGeometry(route.geometry);

      // Use actual route duration from API, but speed up 2x for better viewing
      const animationDuration = (route.duration * 1000) / 2; // Convert to ms and 2x faster

      truck.animateTruck(route.geometry, animationDuration);
    } catch (error) {
      alert('Failed to calculate route. Please check your waypoints.');
    }
  };

  // Dynamic color change functionality (currently not exposed in UI)
  // Keeping for future use
  // const handleColorChange = useCallback((layerId, colorProperty, newColor, classValue = null) => {
  //   ... implementation ...
  // }, []);

  // -----------------------------------
  // 3D Toggle Handler
  // -----------------------------------
  const handleToggle3D = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const newState = !is3DEnabled;
    setIs3DEnabled(newState);

    if (newState) {
      // Enable 3D
      if (map._enable3D) {
        map._enable3D();
      }
    } else {
      // Disable 3D
      if (map._disable3D) {
        map._disable3D();
      }
    }
  }, [is3DEnabled]);

  const handleApplyBuilding3DSettings = useCallback((nextSettings) => {
    const saved = saveBuilding3DSettings(nextSettings);
    setBuilding3DSettings(saved);
    const map = mapRef.current;
    if (!map) return;
    map._building3DSettings = saved;
    if (map._applyBuilding3DSettings) {
      map._applyBuilding3DSettings(saved);
    }
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map._building3DSettings = building3DSettings;
    if (map._applyBuilding3DSettings) {
      map._applyBuilding3DSettings(building3DSettings);
    }
  }, [building3DSettings]);

  // -----------------------------------
  // Current Location Handler
  // -----------------------------------
  const handleCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      return;
    }

    // Check if permission is already granted
    let permissionGranted = false;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        permissionGranted = permission.state === 'granted';
      }
    } catch (e) {
      // Permissions API not supported, continue anyway
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;

        // Update user location state
        setUserLocation({ lat, lng });

        if (mapRef.current) {
          // Update or add current location marker
          if (currentLocationMarkerRef.current) {
            currentLocationMarkerRef.current.setLngLat([lng, lat]);
          } else {
            // Add marker if it doesn't exist
            const el = document.createElement('div');
            el.className = 'current-location-marker';
            el.innerHTML = `
              <div class="current-location-pulse"></div>
              <div class="current-location-dot"></div>
            `;

            const marker = new maplibregl.Marker({
              element: el,
              anchor: 'center'
            })
              .setLngLat([lng, lat])
              .addTo(mapRef.current);

            // Ensure current location pin renders above POI icons
            const locEl2 = marker.getElement();
            if (locEl2) locEl2.style.zIndex = '1500';

            currentLocationMarkerRef.current = marker;
          }

          // Fly to location
          mapRef.current.flyTo({ center: [lng, lat], zoom: 15, duration: 1500 });
        }
      },
      (error) => {
        // Do not change map position if current location cannot be fetched
      },
      {
        enableHighAccuracy: true,
        timeout: 3000, // Cancel if location fetch takes more than 3s
        maximumAge: permissionGranted ? 60000 : 0
      }
    );
  }, []);

  // -----------------------------------
  // FEEDBACK HANDLER
  // -----------------------------------
  const handleFeedbackClick = useCallback(() => {
    // Get current popup data
    const currentData = reverseGeocode.popupData;
    
    if (!currentData) {
      return;
    }

    if (currentData.source === 'REVERSE_GEOCODE') {
      setShowRvgFeedbackModal(true);
      return;
    }

    // Track the selected result for search feedback
    searchTracking.trackSelection({
      entity_id: currentData.entity_id || currentData.id || '',
      display_text: currentData.display_text || currentData.display_address || '',
      full_address: currentData.full_address || currentData.display_address || '',
      lat: currentData.location?.lat || currentData.clickedLocation?.lat,
      lng: currentData.location?.lon || currentData.location?.long || currentData.clickedLocation?.long,
      score: currentData.score || 0
    });

    setShowFeedbackModal(true);
  }, [reverseGeocode.popupData, searchTracking]);

  // -----------------------------------
  // AUTHENTICATION CHECK
  // -----------------------------------
  // Show login screen if not authenticated (after all hooks are called)
  if (!initialized || !authenticated) {
    return <Login />;
  }

  // -----------------------------------
  // UI
  // -----------------------------------
  return (
    <>
      {/* User Badge with Logout */}
      <UserBadge onOpenChange={setIsUserBadgeOpen} />
      
      {/* Top Controls Container (Search + POI) */}
      <div className={`top-controls-container ${routing.isRoutingMode ? 'routing-active' : ''} ${poi.showPoiSidebar ? 'poi-sidebar-active' : ''}`}>
        {/* Search Bar - Modular Component - Hidden on desktop during routing, but shown on mweb */}
        {!poi.showPoiSidebar && ((!routing.isRoutingMode && !routing.routeDetails) || isMweb) && (
          <SearchBar
            onResultSelect={(result) => {
              searchTracking.trackSelection(result);
              handleResultSelect(result);
            }}
            onPoiSuggestionSelect={handlePoiSuggestionSelect}
            isRoutingMode={routing.isRoutingMode}
            mapRef={mapRef}
            onDirectionsFromSearch={handleDirectionsFromSearch}
            onToggleRouting={() => {
              handleSearchBarDirections();
            }}
            onSearchPerformed={(query, results) => {
              searchTracking.trackSearch(query, results);
            }}
            clearSignal={searchClearSignal}
          />
        )}

        {/* Street View Toggle Button */}
        {/* Street View Toggle Button - Hidden (using ZoomControls instead) */}
        {false && !(routing.isRoutingMode && routing.routeDetails) && (
          <button
            className={`street-view-toggle-btn ${streetView.isStreetViewEnabled ? 'active' : ''}`}
            onClick={() => {
              const newState = !streetView.isStreetViewEnabled;
              streetView.setIsStreetViewEnabled(newState);

              // If enabling street view, pan to the target location
              if (newState && mapRef.current) {
                // Target location: lat 28.25, lon 76.85 (center of the example area)
                const targetLat = 28.25;
                const targetLon = 76.85;
                const targetZoom = 14;

                mapRef.current.flyTo({
                  center: [targetLon, targetLat],
                  zoom: targetZoom,
                  duration: 1500,
                  essential: true
                });
              }
            }}
            title={streetView.isStreetViewEnabled ? 'Hide Street View' : 'Show Street View'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 16v-4"></path>
              <path d="M12 8h.01"></path>
            </svg>
            <span>Street View</span>
          </button>
        )}

        {/* Road Quality View Toggle Button - Hidden (using ZoomControls instead) */}
        {false && !(routing.isRoutingMode && routing.routeDetails) && (
          <button
            className={`road-quality-toggle-btn ${roadQuality.isRoadQualityEnabled ? 'active' : ''}`}
            onClick={() => {
              roadQuality.toggleRoadQuality();
            }}
            title={roadQuality.isRoadQualityEnabled ? 'Hide Road Quality View' : 'Show Road Quality View'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18"></path>
              <path d="M3 6h18"></path>
              <path d="M3 18h18"></path>
            </svg>
            <span>Road Quality View</span>
          </button>
        )}
      </div>

      {/* POI Sidebar - Shows list of POIs for selected category */}
      <POISidebar
        show={poi.showPoiSidebar}
        pois={poi.sidebarPois}
        selectedCategory={poi.sidebarCategory}
        userLocation={userLocation}
        onClose={() => {
          poi.setShowPoiSidebar(false);
          poi.setSidebarPois([]);
          poi.setSidebarCategory(null);
          poi.setSelectedAmenities([]);
        }}
        onDirections={async (lat, lng, name) => {
          // Close POI sidebar
          poi.setShowPoiSidebar(false);
          poi.setSidebarPois([]);
          poi.setSidebarCategory(null);
          poi.setSelectedAmenities([]);

          // Close RVG sidebar if open
          reverseGeocode.setShowSidebar(false);
          reverseGeocode.setRvgResults([]);
          reverseGeocode.clearRvgPolygon();
          // Remove search result marker
          if (searchResultMarkerRef.current) {
            searchResultMarkerRef.current.remove();
            searchResultMarkerRef.current = null;
          }
          setIsRvgMode(false);

          // Enter routing mode if not already
          if (!routing.isRoutingMode) {
            routing.toggleRoutingMode();
          }

          // Set destination (Point B) with POI name details
          const destination = { lat, lng };
          const poiName = name || 'Destination';
          const destDetails = {
            entity_name: poiName,
            display_name: poiName,
            rooftop_name: poiName,
            name: poiName,
            location: { lat, lon: lng, long: lng }
          };
          routing.setRoutePointB(destination);
          setRoutePointBDetails(destDetails);
          routing.addRouteMarker(lat, lng, 'B', poiName);

          // Set source (Point A) to user location if available
          if (userLocation) {
            const sourceDetails = getUserLocationDetails(userLocation);
            routing.setRoutePointA(userLocation);
            setRoutePointADetails(sourceDetails);
            routing.addRouteMarker(userLocation.lat, userLocation.lng, 'A', 'Your Location');

            // Fetch and display route with pre-fetched details
            await routing.fetchRoute(userLocation, destination, sourceDetails, destDetails);
          } else {
            // No user location - prefill destination name so user can enter source
            routing.setRoutePointA(null);
            setPrefilledRoutingDestinationName(poiName);
          }
        }}
      />

      {/* Map Controls - Modular Component - HIDDEN */}
      {/* <MapControls
        isRoutingMode={routing.isRoutingMode}
        onToggleRouting={() => {
          if (routing.isRoutingMode) {
            // Exiting routing mode - re-enable RVG
            routing.toggleRoutingMode();
      } else {
            // Entering routing mode - clear RVG markers, sidebar, and search marker
            reverseGeocode.setShowSidebar(false);
            reverseGeocode.setRvgResults([]);
            reverseGeocode.setHighlightedResultIndex(null);
            reverseGeocode.clearRvgMarkers();
            if (searchMarkerRef.current) {
              searchMarkerRef.current.remove();
              searchMarkerRef.current = null;
            }
            routing.toggleRoutingMode();
          }
        }}
        onCurrentLocation={async () => {
          if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
          }

          // Check if permission is already granted
          let permissionGranted = false;
          try {
            if (navigator.permissions && navigator.permissions.query) {
              const permission = await navigator.permissions.query({ name: 'geolocation' });
              permissionGranted = permission.state === 'granted';
            }
          } catch (e) {
            // Permissions API not supported, continue anyway
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lng = position.coords.longitude;
              const lat = position.coords.latitude;

              // Update user location state
              setUserLocation({ lat, lng });

              if (mapRef.current) {
                // Update or add current location marker
                if (currentLocationMarkerRef.current) {
                  currentLocationMarkerRef.current.setLngLat([lng, lat]);
                } else {
                  // Add marker if it doesn't exist
                  const el = document.createElement('div');
                  el.className = 'current-location-marker';
                  el.innerHTML = `
                    <div class="current-location-pulse"></div>
                    <div class="current-location-dot"></div>
                  `;

                  const marker = new maplibregl.Marker({
                    element: el,
                    anchor: 'center'
                  })
                    .setLngLat([lng, lat])
                    .addTo(mapRef.current);

                  // Ensure current location pin renders above POI icons
                  const locEl3 = marker.getElement();
                  if (locEl3) locEl3.style.zIndex = '1500';

                  currentLocationMarkerRef.current = marker;
                }

                // Fly to location
                mapRef.current.flyTo({ center: [lng, lat], zoom: 15, duration: 1500 });
              }
            },
            (error) => {
              if (error.code === error.PERMISSION_DENIED) {
                alert('Location access denied. Please enable location permissions in your browser settings.');
              } else {
                alert('Unable to get your current location. Please try again.');
              }
            },
            {
              enableHighAccuracy: true, // Use high accuracy for manual request
              timeout: permissionGranted ? 2000 : 5000,
              maximumAge: permissionGranted ? 60000 : 0 // Use cached if granted (1 min), otherwise get fresh
            }
          );
        }}
        // onToggleDesigner={() => setShowMapDesigner(!showMapDesigner)} // Commented out - Design button hidden
      /> */}

      {/* Routing Panel - Modular Component */}
      <RoutingPanel
        ref={routingPanelRef}
        isRoutingMode={routing.isRoutingMode}
        routeDetails={routing.routeDetails}
        routeDetailsLoading={routing.routeDetailsLoading}
        routePointA={routing.routePointA}
        routePointB={routing.routePointB}
        routePointADetails={routePointADetails}
        routePointBDetails={routePointBDetails}
        mapRef={mapRef}
        prefillDestinationName={prefilledRoutingDestinationName}
        userLocation={userLocation}
        focusTarget={routingFocusTarget}
        onActiveInputChange={handleActiveInputChange}
        travelMode={routing.travelMode}
        onTravelModeChange={(mode) => routing.setTravelMode(mode)}
        selectedRouteIndex={selectedRouteIndex}
        onSelectRoute={handleSelectRoute}
        onStepHover={(index) => routeStepMarkersRef.current?.highlightStep(index)}
        onStepLeave={() => routeStepMarkersRef.current?.clearHighlight()}
        onStepClick={(index) => routeStepMarkersRef.current?.focusStep(index)}
        onClose={() => {
          routing.toggleRoutingMode();
          // Re-enable RVG mode when closing directions panel
          setIsRvgMode(true);
          setPrefilledRoutingDestinationName('');
          setRoutePointADetails(null);
          setRoutePointBDetails(null);
          routeStopsRef.current = [];
          setRoutingFocusTarget('source');
          setRoutingActiveInput('source');
          setSelectedRouteIndex(0); // Reset selected route when closing
        }}
        onRoutePointsChange={handleRoutePointsChange}
        onStopsChange={async (stopLocations, skipFetch = false) => {
          routeStopsRef.current = stopLocations;
          if (skipFetch) return; // Skip route fetch when called from drag reorder
          setSelectedRouteIndex(0); // Reset selected route when stops change
          if (routing.routePointA && routing.routePointB) {
            routing.hasZoomedToRouteRef.current = false;
            await routing.fetchRoute(
              routing.routePointA,
              routing.routePointB,
              routePointADetails || routing.routeDetails?.pointADetails,
              routePointBDetails || routing.routeDetails?.pointBDetails,
              stopLocations,
              routing.travelMode // Pass current travel mode
            );
          }
        }}
        onPoiClick={(categoryId) => {
          // Handle POI category click from sidebar - replicate top POI icon behavior
          const categoryObj = POI_CATEGORIES[categoryId.toUpperCase()];
          if (!categoryObj) return;

          const categoryAmenities = categoryObj.amenities;
          const allSelected = categoryAmenities.every(a => poi.selectedAmenities.includes(a));

          // Check if zoom level is below 13
          if (currentZoom < 13) {
            // Zoom to source location (routePointA) at zoom level 14
            if (routing.routePointA && mapRef.current) {
              mapRef.current.flyTo({
                center: [routing.routePointA.lng, routing.routePointA.lat],
                zoom: 14,
                duration: 1500,
                essential: true
              });
            }

            // Select only this category (always select when zooming in)
            poi.setSelectedAmenities(categoryAmenities);
          } else {
            // Normal behavior at zoom 13+
            if (allSelected) {
              // Deselect all (clear all selections)
              poi.setSelectedAmenities([]);
            } else {
              // Select only this category (deselect all others)
              poi.setSelectedAmenities(categoryAmenities);
            }
          }
        }}
      />

      {/* Route Step Markers - Interactive maneuver dots on the route polyline */}
      <RouteStepMarkers
        ref={routeStepMarkersRef}
        mapRef={mapRef}
        routeDetails={routing.routeDetails}
        selectedRouteIndex={selectedRouteIndex}
      />

      <div ref={mapContainer} className="map-container"></div>

      {/* Delhivery Maps Logo - Bottom Left */}
      <div className="map-logo-bottom-left">
        <img src="/icons/_v1delhiveryMapsLogo.png" alt="Delhivery Maps" />
      </div>

      {/* Zoom Controls - New Component */}
      {!isUserBadgeOpen && (
        <ZoomControls
          mapRef={mapRef}
          onToggle3D={handleToggle3D}
          is3DEnabled={is3DEnabled}
          onCurrentLocation={handleCurrentLocation}
          streetView={streetView}
          roadQuality={roadQuality}
          events={events}
          roadWidth={roadWidth}
          onToggleDesigner={() => setShowMapDesigner(!showMapDesigner)}
          isDesignerOpen={showMapDesigner}
          onToggleStylePanel={() => setShowMapStylePanel(!showMapStylePanel)}
          isStylePanelOpen={showMapStylePanel}
          onToggleBuilding3DPanel={() => setShowBuilding3DPanel(!showBuilding3DPanel)}
          isBuilding3DPanelOpen={showBuilding3DPanel}
        />
      )}

      {/* Road Width Control Panel */}
      {roadWidth.isPanelOpen && (
        <RoadWidthPanel
          roadTypes={roadWidth.roadTypes}
          selectedRoadType={roadWidth.selectedRoadType}
          onSelectRoadType={roadWidth.setSelectedRoadType}
          currentZoom={roadWidth.currentZoom}
          startZoomLevel={roadWidth.startZoomLevel}
          widthValues={roadWidth.widthValues}
          onUpdateStartZoom={roadWidth.updateStartZoom}
          onUpdateWidth={roadWidth.updateWidth}
          onApplyChanges={roadWidth.applyChanges}
          onReset={roadWidth.resetWidths}
          onClose={roadWidth.togglePanel}
        />
      )}

      {/* Custom Scale Control */}
      <CustomScale mapRef={mapRef} />

      {/* Attribution */}
      <Attribution />

      {reverseGeocode.showPopup && reverseGeocode.popupData && (
        <RVGBottomCard
          data={reverseGeocode.popupData}
          userLocation={userLocation}
          onClose={handleCloseRvgCard}
          onNavigate={handleNavigateFromRvgCard}
          onFeedbackClick={handleFeedbackClick}
        />
      )}

      {/* RVG Sidebar - Modular Component */}
      <RVGSidebar
        show={reverseGeocode.showSidebar}
        isClosing={reverseGeocode.isClosing}
        loading={reverseGeocode.rvgLoading}
        results={reverseGeocode.rvgResults}
        clickedPoint={reverseGeocode.clickedPoint}
        highlightedIndex={reverseGeocode.highlightedResultIndex}
        onClose={() => {
          reverseGeocode.setIsClosing(true);
          reverseGeocode.setHighlightedResultIndex(null);
          reverseGeocode.clearRvgMarkers();
          reverseGeocode.clearRvgPolygon();
          reverseGeocode.setRvgResults([]);
          // Remove search result marker
          if (searchResultMarkerRef.current) {
            searchResultMarkerRef.current.remove();
            searchResultMarkerRef.current = null;
          }
          setTimeout(() => {
            reverseGeocode.setShowSidebar(false);
            reverseGeocode.setIsClosing(false);
          }, 500);
        }}
        onResultClick={(idx, result) => {
          reverseGeocode.setHighlightedResultIndex(idx);
          setSelectedMetadata(result);
          setShowMetadataModal(true);
        }}
      />

      {/* Metadata Modal - Modular Component */}
      <MetadataModal
        show={showMetadataModal}
        onClose={() => setShowMetadataModal(false)}
        metadata={selectedMetadata}
      />


      {/* Truck Animation Button - Hidden */}
      {false && <button 
        className={`truck-mode-btn ${showTruckPanel ? 'active' : ''}`}
        onClick={() => {
          if (!showTruckPanel) {
            // Entering truck mode - close other modes and clear search marker
            if (routing.isRoutingMode) {
              routing.toggleRoutingMode();
            }
            reverseGeocode.setShowSidebar(false);
            reverseGeocode.clearRvgMarkers();
            if (searchMarkerRef.current) {
              searchMarkerRef.current.remove();
              searchMarkerRef.current = null;
            }
          } else {
            // Exiting truck mode - clear truck data
            truck.clearTruckAnimation();
          }
          setCurrentDrawing(null);
        }}
      />}

      {/* Truck Animation Panel */}
      {showTruckPanel && (
        <TruckAnimationPanel
          waypoints={truck.waypoints}
          isAnimating={truck.isAnimating}
          animationProgress={truck.animationProgress}
          onRemoveWaypoint={(index) => {
            const updated = truck.waypoints.filter((_, idx) => idx !== index);
            truck.setWaypoints(updated);
          }}
          onStartAnimation={handleStartTruckAnimation}
          onStopAnimation={truck.stopAnimation}
          onClear={() => {
            truck.clearTruckAnimation();
            setShowTruckPanel(false);
          }}
        />
      )}

      <DrawingPanel
        isDrawingMode={showDrawingPanel}
        onClose={() => setShowDrawingPanel(false)}
        onStartDrawing={handleStartDrawing}
        onSaveFeature={handleSaveFeature}
        onDeleteFeature={handleDeleteFeature}
        drawnFeatures={userFeatures}
        currentDrawing={currentDrawing}
      />

      {/* Map Designer Panel */}
      {showMapDesigner && (
        <MapDesigner
          mapRef={mapRef}
          onClose={() => setShowMapDesigner(false)}
        />
      )}

      {showMapStylePanel && (
        <MapStylePanel
          mapRef={mapRef}
          onClose={() => setShowMapStylePanel(false)}
        />
      )}

      {showBuilding3DPanel && (
        <Building3DPanel
          initialSettings={building3DSettings}
          onApply={handleApplyBuilding3DSettings}
          onClose={() => setShowBuilding3DPanel(false)}
        />
      )}

      

      {/* Feedback Modal */}
      <FeedbackModal
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        searchQuery={searchTracking.searchQuery}
        searchResults={searchTracking.searchResults}
        selectedResult={searchTracking.selectedResult}
        userEmail={keycloak?.tokenParsed?.email || keycloak?.tokenParsed?.preferred_username || "anonymous@delhivery.com"}
      />

      {/* RVG Feedback Modal */}
      <RVGFeedbackModal
        show={showRvgFeedbackModal}
        onClose={() => setShowRvgFeedbackModal(false)}
        rvgData={reverseGeocode.popupData}
        userEmail={keycloak?.tokenParsed?.email || keycloak?.tokenParsed?.preferred_username || "anonymous@delhivery.com"}
      />
    </>
  );
}

export default App;
